import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { trpc } from "@utils/api";
import { type inferRouterInputs, type inferRouterOutputs } from "@trpc/server";
import { type AppRouter } from "@server/api/root";
import { type RootState } from "@store/index";
import { z } from "zod/v4";
import {
  BlockConfigSchema,
  SpreadsheetConfig,
} from "skedyul-core/features/crm";
import { GetPageResponseSchema } from "skedyul-core/features/crm/features/pages/resources/types";

import { StructuredExpression } from "skedyul-ui/ui";
import type { StructuredFilter } from "skedyul-ui/ui";
import {
  mergeEphemeralQueryWithPageBaseQuery,
  resolveEffectivePageBaseQuery,
} from "@schemas/pages/layout/mergeBaseQueryForSave";
import {
  markKanbanQueryLoaded,
  clearKanbanQuerySignatures,
} from "@schemas/pages/blocks/kanban/querySignature";
import {
  buildKanbanPartitionSpecs,
  partitionKanbanRows,
} from "@schemas/pages/blocks/kanban/partition";
import { isKanbanListBlockData } from "skedyul-core/features/crm/features/pages/features/blocks/resources/types.kanbanData";
import type { KanbanListBlockData } from "skedyul-core/features/crm/features/pages/features/blocks/resources/types.kanbanData";
import {
  kanbanGroupKeysEqual,
  serializeKanbanPartitionKey,
  type KanbanGroupKey,
} from "skedyul-core/features/crm/features/pages/features/blocks/resources/types.kanbanKeys";
import { resolveEffectiveListView } from "@schemas/pages/helpers/resolveListView";
import { materializeInstancePath } from "@schemas/pages/helpers/pagePaths";
import type {
  KanbanSectionConfig,
  KanbanSubsectionConfig,
} from "@schemas/pages/blocks/kanban/types";
import type { ViewType } from "@schemas/pages/query/types";
import { mergeFiltersWithParentScope } from "@schemas/pages/helpers/parentScope";
import { markCalendarWindowLoaded } from "@schemas/pages/blocks/calendar/calendarWindow";
import { mergeCalendarWindowRows } from "@schemas/pages/blocks/calendar/mergeCalendarWindowRows";
import { computeRelationshipIncludesForBlock } from "@schemas/pages/blocks/spreadsheet/columns";

const rememberCreatedInstanceId = (
  state: PageV2State,
  instanceId: string | undefined,
): void => {
  if (!instanceId || instanceId.length === 0) return;
  if (state.recentlyCreatedInstanceIds.includes(instanceId)) return;
  state.recentlyCreatedInstanceIds = [
    ...state.recentlyCreatedInstanceIds.slice(-49),
    instanceId,
  ];
};

// Common field type
interface Field {
  id: string;
  handle: string;
  modelId: string;
  label?: string;
  definition?: {
    type: string;
    options?: Array<{ value: string; id: string; label: string }>;
    regex?: string;
  } | null;
  system?: boolean;
  isUnique?: boolean;
  requirement?: "OPTIONAL" | "ON_CREATE" | "REQUIRED";
  error?: {
    code: "REQUIRED" | "REGEX" | "UNIQUE";
    message: string;
  };
  isLoading?: boolean;
}

// Common block type
interface Block {
  id: string;
  modelId: string;
  title?: string;
  config?: SpreadsheetConfig & {
    type?: string;
    includes?: string[];
  };
}

// Richer block type for reducers that need relationship info
interface PageBlock extends Block {
  relationshipLink?: {
    id?: string;
    cardinality?: "ONE_TO_ONE" | "ONE_TO_MANY" | "MANY_TO_ONE" | string;
  } | null;
  data?: Record<string, unknown> | Record<string, unknown>[];
  pagination?: {
    page: number;
    total: number;
    hasMore: boolean;
    limit: number;
  };
  default?: boolean;
}

// Common breadcrumb type
interface Breadcrumb {
  type: "LIST" | "INSTANCE";
  label: string;
  href: string;
  modelId: string;
}

// Instance type
interface Instance {
  data: Record<string, unknown>;
  generatedLabel: string;
  activity: unknown[];
}

// Extended GetPageResponse type
interface ExtendedGetPageResponse
  extends z.infer<typeof GetPageResponseSchema> {
  instance?: Instance;
}

// Row state type used in list working state maps
type RowState = {
  errors?: Record<string, { code: string; message: string }>;
  saving?: boolean;
  saveError?: boolean;
  label?: string;
  isSelected?: boolean;
  fields?: {
    [fieldHandleOrPath: string]: {
      options?: Array<{
        value: string;
        label: string;
        avatar?: { seed?: string; url?: string };
      }>;
    };
  };
};

// Unified view type across all data views for a block
export type BlockViewType = "spreadsheet" | "kanban" | "calendar" | "form";

// Structured scope for a logical collection of records for a block
export interface BlockCollectionScope {
  blockId: string;
  view: BlockViewType;
  section?: KanbanGroupKey;
  subsection?: KanbanGroupKey;
}

// Superset working state shape shared by list-ish and form-style views
export type BlockWorkingState = {
  // Loading flags
  isFetching?: boolean;
  isSearching?: boolean;
  isLoading?: boolean;

  // Selection (primarily used by list/spreadsheet, but available everywhere)
  selectionMode?: "manual" | "filter";
  selectedIds?: string[];

  // Pagination (per logical collection, not just per block)
  pagination?: {
    page: number;
    total: number;
    hasMore: boolean;
    limit: number;
  };

  // Per-row UI state (errors, per-field options, etc.). Stored as generic
  // records to keep the slice flexible; callers should narrow to RowState
  // where needed.
  rows?: Record<string, unknown>;

  // Form-style per-field UI state (e.g., combobox options), shared by forms
  // and form-like editors in other views (e.g., calendar event editor).
  form?: {
    [fieldHandle: string]: {
      options?: { value: string; label: string }[];
    };
  };
};

// Logical collection of records for a given block + view (+ optional subsection)
export interface RecordsCollection {
  id: string;
  scope: BlockCollectionScope;
  original: Array<Record<string, unknown>>;
  working: Array<Record<string, unknown>>;
  ui: BlockWorkingState;
}

// Map of blockId -> array of records for that block's primary collection
type BlockWorkingData = Record<string, Array<Record<string, unknown>>>;

// Internal helper to build a stable, human-readable collection id without
// leaking view-specific hacks like "KANBAN::" into the state shape.
const makeCollectionId = (scope: BlockCollectionScope): string => {
  const { blockId, view, section, subsection } = scope;
  if (view === "kanban" && section && subsection) {
    return serializeKanbanPartitionKey({ blockId, section, subsection });
  }
  return [blockId, view].join("|");
};

const makeKanbanCollectionScope = (
  blockId: string,
  section: KanbanGroupKey,
  subsection: KanbanGroupKey,
): BlockCollectionScope => ({
  blockId,
  view: "kanban",
  section,
  subsection,
});

// Internal helper to get or lazily create a RecordsCollection for a scope.
const getOrCreateCollection = (
  state: PageV2State,
  scope: BlockCollectionScope,
): RecordsCollection => {
  const id = makeCollectionId(scope);
  const existing = state.blockData.collections[id];
  if (existing) {
    return existing;
  }

  const collection: RecordsCollection = {
    id,
    scope,
    original: [],
    working: [],
    ui: {},
  };

  state.blockData.collections[id] = collection;
  return collection;
};

// Convenience helper for the primary per-block records collection used by list
// views and as the backing store for other views (calendar, form) when they
// operate over the same row set.
const getDefaultCollectionForBlock = (
  state: PageV2State,
  blockId: string,
): RecordsCollection =>
  getOrCreateCollection(state, { blockId, view: "spreadsheet" });

const hydrateKanbanCollectionsFromBlockData = (
  state: PageV2State,
  blockId: string,
  kanbanData: KanbanListBlockData,
): void => {
  for (const section of kanbanData.sections) {
    for (const subsection of section.subsections) {
      const scope = makeKanbanCollectionScope(
        blockId,
        section.group,
        subsection.group,
      );
      const collection = getOrCreateCollection(state, scope);
      collection.working = subsection.data;
      collection.original = subsection.data;
      collection.ui = {
        ...collection.ui,
        isFetching: false,
        pagination: {
          page: subsection.pagination?.page ?? 1,
          total: subsection.pagination?.total ?? subsection.data.length,
          hasMore: subsection.pagination?.hasMore ?? false,
          limit:
            subsection.pagination?.limit ?? kanbanData.pagination?.limit ?? 100,
        },
      };
    }
  }
};

const hydrateListPageCollections = (
  state: PageV2State,
  page: GetPageResponse,
  view: ViewType | undefined,
): void => {
  const defaultBlock =
    page.blocks?.find((block) => block.default) ?? page.blocks?.[0];
  if (!defaultBlock?.id) {
    return;
  }

  const blockId = defaultBlock.id;
  const effectiveView = resolveEffectiveListView({ view, pageData: page });

  if (isKanbanListBlockData(defaultBlock.data)) {
    hydrateKanbanCollectionsFromBlockData(state, blockId, defaultBlock.data);
    return;
  }

  const rows = defaultBlock.data;
  if (!isArrayOfRecords(rows)) {
    return;
  }

  const pagination = defaultBlock.pagination;

  if (effectiveView === "kanban") {
    const cfg = (defaultBlock.config || {}) as {
      section?: KanbanSectionConfig;
      subsection?: KanbanSubsectionConfig;
    };
    const partitionSpecs = buildKanbanPartitionSpecs({
      blockId,
      block: defaultBlock,
      pageData: page,
      sectionConfig: cfg.section,
      subsectionConfig: cfg.subsection,
    });
    const buckets = partitionKanbanRows(rows, partitionSpecs);
    const serverLimit = pagination?.limit ?? 100;
    const serverHasMore =
      typeof pagination?.hasMore === "boolean"
        ? pagination.hasMore
        : rows.length >= serverLimit;

    for (const spec of partitionSpecs) {
      const subsectionRows =
        buckets.get(serializeKanbanPartitionKey(spec.partition)) ?? [];
      const scope = makeKanbanCollectionScope(
        blockId,
        spec.partition.section,
        spec.partition.subsection,
      );
      const collection = getOrCreateCollection(state, scope);
      collection.working = subsectionRows;
      collection.original = subsectionRows;
      collection.ui = {
        ...collection.ui,
        isFetching: false,
        pagination: {
          page: 1,
          total: subsectionRows.length,
          hasMore:
            spec.match.length === 0 &&
            !spec.partition.section.field &&
            Boolean(cfg.section?.field)
              ? false
              : serverHasMore,
          limit: serverLimit,
        },
      };
    }
    return;
  }

  const scope: BlockCollectionScope = {
    blockId,
    view: "spreadsheet",
  };
  const collection = getOrCreateCollection(state, scope);
  collection.working = rows;
  collection.original = rows;
  collection.ui = {
    ...collection.ui,
    isFetching: false,
    ...(pagination
      ? {
          pagination: {
            page: pagination.page,
            total: pagination.total,
            hasMore: pagination.hasMore,
            limit: pagination.limit,
          },
        }
      : {}),
  };
};

// Type guards for runtime-narrowed values
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isArrayOfRecords = (
  value: unknown,
): value is Array<Record<string, unknown>> =>
  Array.isArray(value) &&
  value.every(
    (item) => typeof item === "object" && item !== null && !Array.isArray(item),
  );

// Narrow unknown to a value containing optional errors field
//

/**
 * PageV2 Redux Slice
 *
 * This slice manages page data and includes dual storage for record data:
 * - originalRecordData: The original data from the server
 * - workingRecordData: A working copy that can be modified as the user edits
 *
 * Usage:
 * - Use selectOriginalRecordData() to get the original data
 * - Use selectWorkingRecordData() to get the current working data
 * - Use selectHasRecordDataChanges() to check if there are unsaved changes
 * - Use updateWorkingRecordData() action to update the working copy
 * - Use resetWorkingRecordData() action to reset working copy to original
 */

// Define input and output types from router
type RouterInput = inferRouterInputs<AppRouter>;
type RouterOutput = inferRouterOutputs<AppRouter>;

// Output types
export type GetPageResponse = z.infer<typeof GetPageResponseSchema>;
export type CreateInstanceResponse = RouterOutput["crm"]["createInstance"];
export type UpdateInstanceResponse = RouterOutput["crm"]["updateInstance"];

// Input types
export type GetPageInput = RouterInput["crm"]["getPage"];
type FetchPageArgs = GetPageInput & {
  kanbanQueryKey?: string;
  calendarWindowKey?: string;
};
export type CreateInstanceInput = RouterInput["crm"]["createInstance"];
export type UpdateInstanceInput = RouterInput["crm"]["updateInstance"];
export type DeleteInstancesInput = {
  modelId: string;
  ids?: string[];
  selectionMode?: "manual" | "filter";
  filter?: StructuredExpression;
};

export type AssociateContactInput = RouterInput["crm"]["associateContact"];
export type AssociateContactResponse = RouterOutput["crm"]["associateContact"];
export type InstancePageStructure = NonNullable<
  RouterOutput["crm"]["getPageStructure"]
>;

export type InstancePageStructureEntry = {
  status: "idle" | "loading" | "loaded" | "error";
  structure?: InstancePageStructure;
};

export type CalendarEventInstanceEntry = {
  status: "idle" | "loading" | "ready" | "error";
};

const isEphemeralCalendarInstanceId = (instanceId: string): boolean =>
  !instanceId ||
  instanceId === "new" ||
  instanceId.startsWith("temp") ||
  instanceId.startsWith("draft-");

const extractInstanceFormRow = (
  page: GetPageResponse,
): Record<string, unknown> | null => {
  const blocks = page.blocks ?? [];
  const formBlock =
    blocks.find((block) => {
      const blockType = (
        (block.config as { type?: string } | undefined)?.type || ""
      ).toString();
      return blockType === "form" && block.modelId === page.modelId;
    }) ??
    blocks.find(
      (block) => block.modelId === page.modelId && !block.relationshipLink,
    );

  if (!formBlock) return null;
  const data = formBlock.data;
  if (isRecord(data)) return data;
  if (isArrayOfRecords(data) && data.length > 0) {
    return data[0] ?? null;
  }
  return null;
};

const mergeCalendarEventInstanceRow = (
  state: PageV2State,
  calendarBlockId: string,
  instanceId: string,
  fetchedRow: Record<string, unknown>,
): void => {
  const collection = getDefaultCollectionForBlock(state, calendarBlockId);
  const originalRows = Array.isArray(collection.original)
    ? [...collection.original]
    : [];
  const workingRows = Array.isArray(collection.working)
    ? [...collection.working]
    : [];

  const findIndex = (rows: Array<Record<string, unknown>>) =>
    rows.findIndex((row) => String(row.id ?? "") === instanceId);

  const originalIndex = findIndex(originalRows);
  const existingOriginal =
    originalIndex >= 0 ? originalRows[originalIndex] : undefined;
  const nextOriginal = {
    ...(existingOriginal ?? {}),
    ...fetchedRow,
    id: instanceId,
  };
  if (originalIndex >= 0) {
    originalRows[originalIndex] = nextOriginal;
  } else {
    originalRows.push(nextOriginal);
  }

  const workingIndex = findIndex(workingRows);
  const existingWorking =
    workingIndex >= 0 ? workingRows[workingIndex] : undefined;
  const nextWorking: Record<string, unknown> = {
    ...(existingWorking ?? {}),
    ...fetchedRow,
    id: instanceId,
  };
  // Preserve local unsaved overrides where working already differs from original.
  if (existingWorking && existingOriginal) {
    for (const key of Object.keys(existingWorking)) {
      if (key === "id") continue;
      if (
        JSON.stringify(existingWorking[key]) !==
        JSON.stringify(existingOriginal[key])
      ) {
        nextWorking[key] = existingWorking[key];
      }
    }
  }
  if (workingIndex >= 0) {
    workingRows[workingIndex] = nextWorking;
  } else {
    workingRows.push(nextWorking);
  }

  collection.original = originalRows;
  collection.working = workingRows;
};

// Layout draft for storing uncommitted layout changes
export interface LayoutDraft {
  pageId: string;
  blockId: string;
  originalConfig: z.infer<typeof BlockConfigSchema> | null;
  draftConfig: z.infer<typeof BlockConfigSchema>;
  originalBaseQuery?: GetPageResponse["baseQuery"];
  draftBaseQuery?: GetPageResponse["baseQuery"];
  isDirty: boolean;
}

// Define the state interface
export interface PageV2State {
  data: ExtendedGetPageResponse | null;
  instancePageStructureByModelId: Record<string, InstancePageStructureEntry>;
  calendarEventInstanceById: Record<string, CalendarEventInstanceEntry>;
  recentlyCreatedInstanceIds: string[];
  blockData: {
    // Unified collection-based state (canonical shape for all block views).
    collections: Record<string, RecordsCollection>;
    filters?: Record<string, StructuredExpression>;
    sort?: Record<
      string,
      | { key: string; direction?: "asc" | "desc" }
      | Array<{ key: string; direction?: "asc" | "desc" }>
    >;
  }; // Block-specific data stored via logical collections
  isLoading: boolean;
  isSaving: boolean;
  isTitleLoading: boolean; // Specific loading state for title updates
  error: string | null;
  // Path that getPage reported as missing. Keyed by path so a late rejection
  // can't turn the page the user has since moved to into a 404.
  notFoundPath: string | null;
  // UI state for components like QueryBar (ephemeral per block)
  queryBarUi?: {
    [blockId: string]: {
      q?: string;
      isActive?: boolean;
      calendar?: {
        startField?: string | null;
        endField?: string | null;
        recurrenceField?: string | null;
        seriesIdField?: string | null;
        originalStartField?: string | null;
        sectionField?: string | null;
        sectionGroupingKey?: string | null;
      };
      layout?: {
        selectedFieldValues?: string[];
        kanbanStatusBadgeFieldValue?: string | null;
        kanbanDescriptionFieldValue?: string | null;
      };
    };
  };
  import: {
    isLoading: boolean;
  };
  teamChat: {
    currentScope: string;
    contextTags: string[];
    messages: Array<{
      id: string;
      type: "human" | "ai" | "system" | "progress";
      content: string;
      timestamp: string;
    }>;
    activeSegmentId?: string;
    activeTab: string; // Add active tab state
    isTyping: boolean; // Add typing indicator
  };
  // Layout draft for storing uncommitted layout changes
  layoutDraft: LayoutDraft | null;
}

// Backwards-compatible aliases during migration. Prefer BlockWorkingState
// directly; ListWorkingState may also be null in some legacy call sites.
export type ListWorkingState = BlockWorkingState | null;
export type FormWorkingState = BlockWorkingState;

// Initial state
const initialState: PageV2State = {
  data: null,
  instancePageStructureByModelId: {},
  calendarEventInstanceById: {},
  recentlyCreatedInstanceIds: [],
  blockData: {
    collections: {},
  },
  isLoading: false,
  isSaving: false,
  isTitleLoading: false,
  error: null,
  notFoundPath: null,
  queryBarUi: {},
  import: {
    isLoading: false,
  },
  teamChat: {
    currentScope: "agent",
    contextTags: [],
    messages: [],
    activeTab: "chat",
    isTyping: false,
  },
  layoutDraft: null,
};

const resetKanbanCollectionsForBlock = (
  state: PageV2State,
  blockId: string,
): void => {
  const collections = state.blockData.collections ?? {};
  Object.values(collections).forEach((collection) => {
    if (
      collection.scope.blockId !== blockId ||
      collection.scope.view !== "kanban"
    ) {
      return;
    }

    collection.working = [];
    collection.original = [];
    collection.ui = {
      ...collection.ui,
      isFetching: false,
      pagination: {
        page: 0,
        total: 0,
        hasMore: true,
        limit: collection.ui.pagination?.limit ?? 10,
      },
    };
  });
};

// Helper function to check if a field needs validation
const needsValidation = (field: Field, instanceId?: string): boolean => {
  // Skip validation for optional fields
  if (field.requirement === "OPTIONAL") {
    return false;
  }

  // Skip validation for ON_CREATE fields during updates
  if (field.requirement === "ON_CREATE" && instanceId) {
    return false;
  }

  // Always validate if field is unique, has options, or has regex
  const hasOptions =
    field.definition?.options && field.definition.options.length > 0;
  const hasRegex = field.definition?.regex && field.definition.regex.length > 0;
  return Boolean(field.isUnique === true || hasOptions || hasRegex);
};

// (removed unused helpers: getFieldsForModel, getModelById, getFieldsForBlock, getModelForBlock)

// Async thunk for field validation
export const validateField = createAsyncThunk(
  "pageV2/validateField",
  async (
    {
      fieldId,
      value,
      instanceId,
    }: {
      fieldId: string;
      value: unknown;
      instanceId?: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as { pageV2: PageV2State };
      const pageData = state.pageV2.data;

      if (!pageData) {
        throw new Error("No page data available for validation");
      }

      // Find the field in the page data
      const field = pageData.fields.find((f: Field) => f.id === fieldId);

      if (!field) {
        throw new Error(`Field with ID ${fieldId} not found`);
      }

      // Check if validation is needed
      if (!needsValidation(field, instanceId)) {
        // No validation needed, return success
        return { fieldId, isValid: true, error: null };
      }

      // Call the validateRecord API
      const result = await trpc.crm.validateRecord.mutate({
        fieldId,
        instanceId,
        data: value,
      });

      return {
        fieldId,
        isValid: result.result,
        error: result.error
          ? {
              code: result.error.code,
              message: result.error.message,
            }
          : null,
      };
    } catch (error) {
      return rejectWithValue({
        code: "UNKNOWN",
        message: error instanceof Error ? error.message : "Validation failed",
      });
    }
  },
);

export const fetchInstancePageStructure = createAsyncThunk<
  { modelId: string; structure: InstancePageStructure },
  { modelId: string },
  { rejectValue: string }
>(
  "pageV2/fetchInstancePageStructure",
  async ({ modelId }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { pageV2: PageV2State };
      const models = state.pageV2.data?.models ?? [];
      const model = models.find((m) => m.id === modelId);
      if (!model) {
        return rejectWithValue("Model not found");
      }

      const pageData = state.pageV2.data;
      const instancePagePath =
        pageData?.instancePagePath ?? `${model.handlePlural}/[id]`;

      const structure = await trpc.crm.getPageStructure.query({
        path: materializeInstancePath(instancePagePath, "new"),
      });

      if (!structure || structure.page.type !== "INSTANCE") {
        return rejectWithValue("Instance page not found");
      }

      return { modelId, structure };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to load instance page structure",
      );
    }
  },
  {
    condition: ({ modelId }, { getState }) => {
      const state = getState() as { pageV2: PageV2State };
      const entry = state.pageV2.instancePageStructureByModelId[modelId];
      return !entry || entry.status === "idle" || entry.status === "error";
    },
  },
);

export const fetchCalendarEventInstance = createAsyncThunk<
  {
    instanceId: string;
    calendarBlockId: string;
    row: Record<string, unknown>;
  },
  { modelId: string; instanceId: string; calendarBlockId: string },
  { rejectValue: string }
>(
  "pageV2/fetchCalendarEventInstance",
  async (
    { modelId, instanceId, calendarBlockId },
    { getState, rejectWithValue },
  ) => {
    try {
      if (isEphemeralCalendarInstanceId(instanceId)) {
        return rejectWithValue("Ephemeral calendar event");
      }

      const state = getState() as { pageV2: PageV2State };
      const models = state.pageV2.data?.models ?? [];
      const model = models.find((m) => m.id === modelId);
      if (!model) {
        return rejectWithValue("Model not found");
      }

      const pageData = state.pageV2.data;
      const instancePagePath =
        pageData?.instancePagePath ?? `${model.handlePlural}/[id]`;

      const page = (await trpc.crm.getPage.query({
        path: materializeInstancePath(instancePagePath, instanceId),
      })) as GetPageResponse;

      if (!page || page.type !== "INSTANCE") {
        return rejectWithValue("Instance page not found");
      }

      const row = extractInstanceFormRow(page);
      if (!row) {
        return rejectWithValue("Instance form data not found");
      }

      return {
        instanceId,
        calendarBlockId,
        row: { ...row, id: instanceId },
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to load calendar event instance",
      );
    }
  },
  {
    condition: ({ instanceId }, { getState }) => {
      if (isEphemeralCalendarInstanceId(instanceId)) {
        return false;
      }
      const state = getState() as { pageV2: PageV2State };
      const entry = state.pageV2.calendarEventInstanceById[instanceId];
      return !entry || entry.status === "idle" || entry.status === "error";
    },
  },
);

// Async thunks
const PAGE_NOT_FOUND_ERROR = "Page not found";

export const fetchPage = createAsyncThunk<
  ExtendedGetPageResponse,
  FetchPageArgs,
  { rejectValue: string }
>(
  "pageV2/fetchPage",
  async (input: FetchPageArgs, { rejectWithValue, getState, dispatch }) => {
    try {
      const {
        kanbanQueryKey: _kanbanQueryKey,
        calendarWindowKey: _calendarWindowKey,
        ...queryInput
      } = input;
      const state = getState() as { pageV2: PageV2State };
      const currentData = state.pageV2.data;

      if (state.pageV2.layoutDraft?.draftBaseQuery !== undefined) {
        queryInput.baseQuery = state.pageV2.layoutDraft.draftBaseQuery ?? null;
      }

      // If we have data and it's for the same instance, skip the fetch entirely
      if (currentData && currentData.instance?.data?.id) {
        const currentInstanceId = currentData.instance.data.id;
        // Extract instance ID from the path (assuming path format like "model/instanceId")
        const pathParts = input.path.split("/");
        const requestedInstanceId = pathParts[pathParts.length - 1];

        if (currentInstanceId === requestedInstanceId && currentData) {
          // We already have this data; return the cached data
          return currentData;
        }
      }

      const result = await trpc.crm.getPage.query(queryInput);
      const page = result as ExtendedGetPageResponse;

      if (page.type === "LIST") {
        const defaultBlock =
          page.blocks?.find((block) => block.default) ?? page.blocks?.[0];
        if (defaultBlock?.id) {
          clearKanbanQuerySignatures(defaultBlock.id);
        }
      }

      return page;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch page",
      );
    }
  },
);

export const updateBlockConfig = createAsyncThunk<
  RouterOutput["crm"]["updateBlockConfig"],
  {
    blockId: string;
    config?: z.infer<typeof BlockConfigSchema>;
    view?: "kanban" | "spreadsheet";
    skipRefetch?: boolean;
  },
  { rejectValue: string }
>(
  "pageV2/updateBlockConfig",
  async (
    input: {
      blockId: string;
      config?: z.infer<typeof BlockConfigSchema>;
      view?: "kanban" | "spreadsheet";
      skipRefetch?: boolean;
    },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const result = await trpc.crm.updateBlockConfig.mutate(
        input.view
          ? { blockId: input.blockId, view: input.view }
          : {
              blockId: input.blockId,
              config: input.config as z.infer<typeof BlockConfigSchema>,
            },
      );

      // After successful config update, refetch the current page so Redux reflects the new view/config
      // Skip refetch if explicitly requested (e.g., for field selection which updates UI reactively)
      if (!input.skipRefetch) {
        const state = getState() as { pageV2: PageV2State };
        const path = state.pageV2.data?.path;
        if (path) {
          await dispatch(
            fetchPage({
              path,
              // preserve existing URL-level filters/sort via page effect; here we just request current path
            }),
          );
        }
      }
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to update block config",
      );
    }
  },
);

export const updateQueryBar = createAsyncThunk(
  "pageV2/updateQueryBar",
  async (
    input: {
      blockId: string;
      filters: StructuredExpression;
      sort?:
        | { key: string; direction?: "asc" | "desc" }
        | Array<{ key: string; direction?: "asc" | "desc" }>;
    },
    { getState, dispatch },
  ) => {
    const state = getState() as { pageV2: PageV2State };
    const pageData = state.pageV2.data;

    if (!pageData) {
      return input;
    }

    // Find the block to get its modelId
    const block = pageData.blocks?.find((b) => b.id === input.blockId);

    if (!block?.modelId) {
      return input;
    }

    const collections = state.pageV2.blockData.collections;
    const collection = Object.values(collections).find(
      (c) =>
        c.scope.blockId === input.blockId && c.scope.view === "spreadsheet",
    );
    const limit = collection?.ui.pagination?.limit ?? 50;

    const include = computeRelationshipIncludesForBlock(block, pageData);

    // Use loadMoreBlockItems with replace:true to replace data instead of calling fetchPage
    // This prevents flickering by avoiding the full page fetch that clears all blockData
    await dispatch(
      loadMoreBlockItems({
        modelId: block.modelId,
        blockId: input.blockId,
        page: 1, // Always start from page 1 when filters change
        limit,
        filters: input.filters,
        ...(input.sort ? { sort: input.sort } : {}),
        ...(include.length > 0 ? { include } : {}),
        replace: true, // Replace existing data instead of concatenating
      }),
    );

    return input;
  },
);

export const fetchRelationOptions = createAsyncThunk(
  "pageV2/fetchRelationOptions",
  async (
    input: {
      blockId: string;
      rowId: string;
      fieldHandle: string;
      targetModelId: string;
      query?: string;
      cache?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const { targetModelId, query } = input;
      const result = await trpc.crm.getInstances.query({
        modelId: targetModelId,
        filters: query
          ? {
              search: {
                contains: query.trim(),
              },
            }
          : undefined,
        page: 1,
        limit: query && query.trim().length > 0 ? 10 : 5,
      });

      // Define Zod schemas for instance metadata
      const ImageSchema = z
        .object({
          url: z.string().optional(),
          seed: z.string().optional(),
        })
        .passthrough();

      const InstanceMetaSchema = z
        .object({
          label: z.string().optional(),
          image: ImageSchema.nullable().optional(),
          model_id: z.string().optional(),
        })
        .passthrough();

      const InstanceLikeSchema = z
        .object({
          id: z.string(),
          label: z.string().optional(),
          _meta: InstanceMetaSchema.optional(),
        })
        .passthrough();

      type ImageType = z.infer<typeof ImageSchema>;
      type InstanceLikeType = z.infer<typeof InstanceLikeSchema>;

      const items = (Array.isArray(result.data) ? result.data : [])
        .map((value) => {
          const parseResult = InstanceLikeSchema.safeParse(value);
          if (!parseResult.success) return null;
          return parseResult.data;
        })
        .filter((instance): instance is InstanceLikeType => instance !== null)
        .map((instance) => {
          // Get label from _meta or fallback to id (also fallback if label is empty string)
          const metaLabel = instance._meta?.label;
          const label =
            metaLabel && metaLabel.trim() !== "" ? metaLabel : instance.id;

          // Handle image data with proper type checking
          const img = instance._meta?.image;
          let avatar: ImageType | undefined;

          if (img) {
            if (img.url) {
              avatar = { url: img.url };
            } else if (img.seed) {
              avatar = { seed: img.seed };
            }
          }

          const result = {
            value: instance.id,
            label,
            ...(avatar ? { avatar } : {}),
          };

          return result;
        });

      return { ...input, options: items };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch options",
      );
    }
  },
);

// Load more items for a specific block (append to existing data)
export const loadMoreBlockItems = createAsyncThunk(
  "pageV2/loadMoreBlockItems",
  async (
    input: {
      modelId: string;
      blockId: string;
      page: number;
      limit: number;
      filters?: StructuredExpression;
      sort?:
        | { key: string; direction?: "asc" | "desc" }
        | Array<{ key: string; direction?: "asc" | "desc" }>;
      include?: string[];
      replace?: boolean; // If true, replace existing data instead of concatenating
      calendarWindow?: {
        startAt: string;
        endAt: string;
        window: string;
        sessionIdentifiers: {
          start: string;
          end?: string;
        };
      };
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const { modelId, blockId, page, limit, filters, sort } = input;
      const pageV2State = (getState() as { pageV2: PageV2State }).pageV2;
      const mergedQuery = mergeEphemeralQueryWithPageBaseQuery(
        resolveEffectivePageBaseQuery(
          pageV2State.data,
          pageV2State.layoutDraft,
        ),
        filters,
        sort,
      );
      let include: string[] | undefined = input.include;

      // Fallback: compute includes from current page state when not provided
      if (!include || include.length === 0) {
        const state = getState() as { pageV2: PageV2State };
        const pageData = state.pageV2.data;
        const block = pageData?.blocks?.find((b) => b.id === blockId);
        if (pageData && block) {
          const arr = computeRelationshipIncludesForBlock(block, pageData);
          if (arr.length > 0) include = arr;
        }
      }

      const result = await trpc.crm.getInstances.query({
        modelId,
        page,
        limit,
        full: true,
        activeWorkflows: true,
        ...(mergedQuery.filters ? { filters: mergedQuery.filters } : {}),
        ...(mergedQuery.sort ? { sort: mergedQuery.sort } : {}),
        ...(Array.isArray(include) && include.length > 0 ? { include } : {}),
        ...(input.calendarWindow
          ? { calendarWindow: input.calendarWindow }
          : {}),
      });

      const isArray = Array.isArray(result?.data);
      const newData: Array<Record<string, unknown>> = isArray
        ? (result.data as Array<Record<string, unknown>>)
        : [];
      const newPagination = result?.pagination as
        | { page?: number; total?: number; hasMore?: boolean; limit?: number }
        | undefined;

      return {
        blockId,
        newData,
        pagination: newPagination,
        requestedPage: page,
        requestedLimit: limit,
        replace: input.replace ?? false,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to load more items",
      );
    }
  },
);

// Load items for a specific Kanban subsection (independent pagination per subsection)
export const loadKanbanSubsectionItems = createAsyncThunk(
  "pageV2/loadKanbanSubsectionItems",
  async (
    input: {
      blockId: string;
      section: KanbanGroupKey;
      subsection: KanbanGroupKey;
      modelId: string;
      page: number;
      limit: number;
      filters?: StructuredExpression;
      sort?:
        | { key: string; direction?: "asc" | "desc" }
        | Array<{ key: string; direction?: "asc" | "desc" }>;
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const { modelId, page, limit, filters, sort } = input;
      const pageV2State = (getState() as { pageV2: PageV2State }).pageV2;
      const mergedQuery = mergeEphemeralQueryWithPageBaseQuery(
        resolveEffectivePageBaseQuery(
          pageV2State.data,
          pageV2State.layoutDraft,
        ),
        filters,
        sort,
      );
      const result = await trpc.crm.getInstances.query({
        modelId,
        page,
        limit,
        full: true,
        ...(mergedQuery.filters ? { filters: mergedQuery.filters } : {}),
        ...(mergedQuery.sort ? { sort: mergedQuery.sort } : {}),
      });

      const isArray = Array.isArray(result?.data);
      const newData: Array<Record<string, unknown>> = isArray
        ? (result.data as Array<Record<string, unknown>>)
        : [];

      return {
        ...input,
        newData,
        pagination: result?.pagination as
          | { page?: number; total?: number; hasMore?: boolean; limit?: number }
          | undefined,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to load subsection",
      );
    }
  },
);

export const loadKanbanSubsectionItemsBulk = createAsyncThunk(
  "pageV2/loadKanbanSubsectionItemsBulk",
  async (
    inputs: Array<{
      blockId: string;
      section: KanbanGroupKey;
      subsection: KanbanGroupKey;
      modelId: string;
      page: number;
      limit: number;
      filters?: StructuredExpression;
      sort?:
        | { key: string; direction?: "asc" | "desc" }
        | Array<{ key: string; direction?: "asc" | "desc" }>;
    }>,
    { rejectWithValue, getState },
  ) => {
    try {
      const pageV2State = (getState() as { pageV2: PageV2State }).pageV2;
      const effectiveBaseQuery = resolveEffectivePageBaseQuery(
        pageV2State.data,
        pageV2State.layoutDraft,
      );
      // Process all inputs in parallel
      const promises = inputs.map(async (input) => {
        const { modelId, page, limit, filters, sort } = input;
        const mergedQuery = mergeEphemeralQueryWithPageBaseQuery(
          effectiveBaseQuery,
          filters,
          sort,
        );
        const result = await trpc.crm.getInstances.query({
          modelId,
          page,
          limit,
          full: true,
          ...(mergedQuery.filters ? { filters: mergedQuery.filters } : {}),
          ...(mergedQuery.sort ? { sort: mergedQuery.sort } : {}),
        });

        const isArray = Array.isArray(result?.data);
        const newData: Array<Record<string, unknown>> = isArray
          ? (result.data as Array<Record<string, unknown>>)
          : [];

        return {
          ...input,
          newData,
          pagination: result?.pagination as
            | {
                page?: number;
                total?: number;
                hasMore?: boolean;
                limit?: number;
              }
            | undefined,
        };
      });

      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to load subsections",
      );
    }
  },
);

export const sendTeamChatMessage = createAsyncThunk(
  "pageV2/sendTeamChatMessage",
  async (message: string, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as { pageV2: PageV2State };
      const pageData = state.pageV2.data;

      if (!pageData) {
        throw new Error("No page data available");
      }

      // Get the page ID from the current page data
      const pageId = pageData.id;
      const instanceId = pageData.instance?.data?.id;

      // Prepare the request payload
      const workingCollections = state.pageV2.blockData.collections;
      const workingDataByBlock: BlockWorkingData = Object.values(
        workingCollections,
      ).reduce<BlockWorkingData>((acc, collection) => {
        if (collection.scope.view !== "spreadsheet") return acc;
        acc[collection.scope.blockId] = collection.working;
        return acc;
      }, {});

      const payload = {
        messages: [
          ...state.pageV2.teamChat.messages.map((msg) => ({
            role:
              msg.type === "human"
                ? "user"
                : ("assistant" as "user" | "assistant"),
            content: msg.content,
          })),
          {
            role: "user" as const,
            content: message,
          },
        ],
        context: {
          pageId,
          instanceId,
        },
        workingData: workingDataByBlock,
      };

      // Call the team-chat API
      const response = await fetch("/api/ai/team-chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No reader available");
      }

      const decoder = new TextDecoder();
      let buffer = "";
      let aiResponse = "";
      let finalWorkingData: BlockWorkingData = workingDataByBlock;
      const progressMessages: string[] = [];
      const aiMessageId = `ai-${Date.now()}`;

      // Process the stream
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              interface TeamChatStreamChunk {
                type?: "progress" | "text" | "final" | "error";
                error?: string;
                message?: string;
                workingData?: BlockWorkingData;
                done?: boolean;
              }
              const data = JSON.parse(line.slice(6)) as TeamChatStreamChunk;

              if (data.error) {
                throw new Error(String(data.error));
              }

              // Handle different message types
              if (data.type === "progress" && data.message) {
                progressMessages.push(data.message);
                // Dispatch progress update immediately
                dispatch(
                  updateTeamChatProgressMessage({
                    id: `progress-${Date.now()}-${progressMessages.length}`,
                    content: data.message,
                  }),
                );
              } else if (data.type === "text" && data.message !== undefined) {
                aiResponse = data.message;
                // Dispatch streaming text update immediately
                dispatch(
                  updateTeamChatStreamingMessage({
                    id: aiMessageId,
                    content: data.message,
                  }),
                );
              } else if (data.type === "final") {
                if (data.message !== undefined) {
                  aiResponse = data.message;
                }
                if (data.workingData) {
                  finalWorkingData = data.workingData;
                  // TODO: Update to handle block-specific data updates
                  // For now, update all blocks with the working data
                  if (state.pageV2.data?.blocks) {
                    state.pageV2.data.blocks.forEach((block) => {
                      if (block.id) {
                        const blockSpecific = data.workingData?.[block.id];
                        if (typeof blockSpecific !== "undefined") {
                          dispatch(
                            updateWorkingBlockData({
                              blockId: block.id,
                              data: blockSpecific,
                            }),
                          );
                        }
                      }
                    });
                  }
                }
                if (data.done) {
                  return {
                    success: true,
                    message,
                    aiResponse,
                    workingData: finalWorkingData,
                    progressMessages,
                  };
                }
              } else if (data.type === "error") {
                throw new Error(data.error || "An error occurred");
              } else if (!data.type) {
                // Handle legacy format for backward compatibility
                if (data.message !== undefined) {
                  aiResponse = data.message;
                  // Dispatch streaming text update immediately
                  dispatch(
                    updateTeamChatStreamingMessage({
                      id: aiMessageId,
                      content: data.message,
                    }),
                  );
                }
                if (data.workingData) {
                  finalWorkingData = data.workingData;
                  // TODO: Update to handle block-specific data updates
                  // For now, update all blocks with the working data
                  if (state.pageV2.data?.blocks) {
                    state.pageV2.data.blocks.forEach((block) => {
                      if (block.id) {
                        const blockSpecific = data.workingData?.[block.id];
                        if (typeof blockSpecific !== "undefined") {
                          dispatch(
                            updateWorkingBlockData({
                              blockId: block.id,
                              data: blockSpecific,
                            }),
                          );
                        }
                      }
                    });
                  }
                }
                if (data.done) {
                  return {
                    success: true,
                    message,
                    aiResponse,
                    workingData: finalWorkingData,
                    progressMessages,
                  };
                }
              }
            } catch (e) {
              console.error("Error parsing stream data:", e);
            }
          }
        }
      }

      return {
        success: true,
        message,
        aiResponse,
        workingData: finalWorkingData,
        progressMessages,
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to send team chat message",
      );
    }
  },
);

export const createInstance = createAsyncThunk<
  unknown,
  CreateInstanceInput,
  { rejectValue: unknown }
>(
  "pageV2/createInstance",
  async (input: CreateInstanceInput, { rejectWithValue }) => {
    try {
      const resultUnknown: unknown =
        await trpc.crm.createInstance.mutate(input);

      // Check if the result has validation errors
      if (typeof resultUnknown === "object" && resultUnknown !== null) {
        const maybeErrors = (
          resultUnknown as { errors?: Record<string, unknown> | null }
        ).errors;
        if (maybeErrors && Object.keys(maybeErrors).length > 0) {
          return rejectWithValue(maybeErrors);
        }
      }

      return resultUnknown;
    } catch (error) {
      return rejectWithValue({
        _: {
          code: "UNKNOWN",
          message:
            error instanceof Error
              ? error.message
              : "Failed to create instance",
        },
      });
    }
  },
);

export const updateInstance = createAsyncThunk<
  unknown,
  UpdateInstanceInput,
  { rejectValue: unknown }
>(
  "pageV2/updateInstance",
  async (input: UpdateInstanceInput, { rejectWithValue }) => {
    try {
      const resultUnknown: unknown =
        await trpc.crm.updateInstance.mutate(input);

      // Check if the result has validation errors
      if (typeof resultUnknown === "object" && resultUnknown !== null) {
        const maybeErrors = (
          resultUnknown as { errors?: Record<string, unknown> | null }
        ).errors;
        if (maybeErrors && Object.keys(maybeErrors).length > 0) {
          return rejectWithValue(maybeErrors);
        }
      }

      return resultUnknown;
    } catch (error) {
      return rejectWithValue({
        _: {
          code: "UNKNOWN",
          message:
            error instanceof Error
              ? error.message
              : "Failed to update instance",
        },
      });
    }
  },
);

export const deleteInstances = createAsyncThunk(
  "pageV2/deleteInstances",
  async (input: DeleteInstancesInput, { rejectWithValue }) => {
    try {
      const result = await trpc.crm.deleteInstances.mutate(input);

      return result;
    } catch (error) {
      return rejectWithValue({
        _: {
          code: "UNKNOWN",
          message:
            error instanceof Error
              ? error.message
              : "Failed to delete instances",
        },
      });
    }
  },
);

export const updateModelLabelTemplate = createAsyncThunk(
  "pageV2/updateModelLabelTemplate",
  async (
    input: {
      modelId: string;
      labelTemplate: string;
      instanceId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await trpc.crm.updateModelLabelTemplate.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to update model label template",
      );
    }
  },
);

export const updatePageTitle = createAsyncThunk(
  "pageV2/updatePageTitle",
  async (
    input: {
      pageId: string;
      title: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const result = await trpc.crm.updatePageTitle.mutate(input);
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to update page title",
      );
    }
  },
);

export const changeListPageModel = createAsyncThunk(
  "pageV2/changeListPageModel",
  async (
    input: {
      pageId: string;
      modelId: string;
    },
    { rejectWithValue, dispatch, getState },
  ) => {
    try {
      const result = await trpc.crm.changeListPageModel.mutate(input);
      const state = getState() as { pageV2: PageV2State };
      const path = state.pageV2.data?.path;
      if (path) {
        await dispatch(
          fetchPage({
            path,
          }),
        );
      }
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to change list page model",
      );
    }
  },
);

// Associate a contact to the current instance via a specific channel
export const associateContact = createAsyncThunk(
  "pageV2/associateContact",
  async (
    input: {
      communicationChannelId: string;
      instanceId: string;
      channelModelLinkInstanceId: string;
      contactId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const association = await trpc.crm.associateContact.mutate(input);

      return association;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to associate contact",
      );
    }
  },
);

const applyLayoutDraftToPageData = (state: PageV2State): void => {
  const draft = state.layoutDraft;
  if (!draft || !state.data) {
    return;
  }

  const blockIndex = state.data.blocks.findIndex(
    (block: Block) => block.id === draft.blockId,
  );
  if (blockIndex !== -1 && state.data.blocks[blockIndex]) {
    state.data.blocks[blockIndex]!.config = draft.draftConfig;
  }
  if (draft.draftBaseQuery !== undefined) {
    state.data.baseQuery = draft.draftBaseQuery ?? null;
  }
};

// Create the slice
export const pageV2Slice = createSlice({
  name: "pageV2",
  initialState,
  reducers: {
    clearPageError: (state) => {
      state.error = null;
    },
    syncPagePresentationLocally: (
      state,
      action: PayloadAction<{ title?: string; icon?: string | null }>,
    ) => {
      if (!state.data) return;

      if (action.payload.title !== undefined) {
        const nextTitle = action.payload.title;
        state.data.title = nextTitle;
        state.data.breadcrumbs = state.data.breadcrumbs.map(
          (breadcrumb, index, breadcrumbs) => {
            const isLast = index === breadcrumbs.length - 1;
            if (isLast && breadcrumb.type === "LIST") {
              return {
                ...breadcrumb,
                label: nextTitle,
              };
            }
            return breadcrumb;
          },
        );
      }

      if (action.payload.icon !== undefined) {
        state.data.icon = action.payload.icon;
      }
    },
    updateQueryBarUi: (
      state,
      action: PayloadAction<{
        blockId: string;
        patch: {
          q?: string;
          isActive?: boolean;
          calendar?: {
            startField?: string | null;
            endField?: string | null;
            recurrenceField?: string | null;
            seriesIdField?: string | null;
            originalStartField?: string | null;
            sectionField?: string | null;
            sectionGroupingKey?: string | null;
          };
          layout?: {
            selectedFieldValues?: string[];
            kanbanStatusBadgeFieldValue?: string | null;
            kanbanDescriptionFieldValue?: string | null;
          };
        };
      }>,
    ) => {
      const { blockId, patch } = action.payload;
      const current = state.queryBarUi?.[blockId] || {};
      state.queryBarUi = state.queryBarUi || {};
      state.queryBarUi[blockId] = {
        ...current,
        ...patch,
        calendar: {
          ...(current as { calendar?: Record<string, unknown> }).calendar,
          ...patch.calendar,
        },
        layout: {
          ...(current as { layout?: Record<string, unknown> }).layout,
          ...patch.layout,
        },
      };
    },
    clearPageData: (state) => {
      state.data = null;
      state.blockData = initialState.blockData;
      state.calendarEventInstanceById = {};
      state.recentlyCreatedInstanceIds = [];
      state.teamChat = {
        currentScope: "agent",
        contextTags: [],
        messages: [],
        activeTab: "chat",
        isTyping: false,
      };
    },
    invalidateKanbanCollections: (
      state,
      action: PayloadAction<{ blockId: string }>,
    ) => {
      resetKanbanCollectionsForBlock(state, action.payload.blockId);
      const block = state.data?.blocks?.find(
        (entry) => entry.id === action.payload.blockId,
      );
      if (block && isKanbanListBlockData(block.data)) {
        block.data = {
          view: "kanban",
          sections: [],
        };
      }
    },
    syncKanbanCollectionsFromBlockData: (
      state,
      action: PayloadAction<{ blockId: string }>,
    ) => {
      const block = state.data?.blocks?.find(
        (entry) => entry.id === action.payload.blockId,
      );
      if (block && isKanbanListBlockData(block.data)) {
        hydrateKanbanCollectionsFromBlockData(
          state,
          action.payload.blockId,
          block.data,
        );
      }
    },
    syncBlockQueryCache: (
      state,
      action: PayloadAction<{
        blockId: string;
        filters?: StructuredExpression;
        sort?:
          | { key: string; direction?: "asc" | "desc" }
          | Array<{ key: string; direction?: "asc" | "desc" }>;
      }>,
    ) => {
      const { blockId, filters, sort } = action.payload;
      state.blockData.filters = state.blockData.filters ?? {};
      if (filters !== undefined) {
        state.blockData.filters[blockId] = filters;
      }
      if (sort !== undefined) {
        state.blockData.sort = state.blockData.sort ?? {};
        state.blockData.sort[blockId] = sort;
      } else if (state.blockData.sort?.[blockId]) {
        const nextSort = { ...state.blockData.sort };
        delete nextSort[blockId];
        state.blockData.sort = nextSort;
      }
    },
    setPageData: (
      state,
      action: PayloadAction<{
        structure: unknown;
        data: unknown;
      }>,
    ) => {
      state.isLoading = false;
    },
    setImportLoading: (state, action: PayloadAction<boolean>) => {
      state.import.isLoading = action.payload;
    },
    updateWorkingBlockData: (
      state,
      action: PayloadAction<{
        blockId: string;
        data: Record<string, unknown> | Record<string, unknown>[];
      }>,
    ) => {
      const { blockId, data } = action.payload;

      // Keep unified collection in sync for this block
      const collection = getDefaultCollectionForBlock(state, blockId);
      const normalized: Array<Record<string, unknown>> = Array.isArray(data)
        ? data
        : isRecord(data)
          ? [data]
          : [];
      collection.working = normalized;
      // If we don't yet have an original snapshot, initialize it from the
      // incoming working data so that consumers always see a consistent pair.
      if (!collection.original || collection.original.length === 0) {
        collection.original = normalized;
      }
    },
    syncKanbanItemMove: (
      state,
      action: PayloadAction<{
        blockId: string;
        instanceId: string;
        sourceSection: KanbanGroupKey;
        sourceSubsection: KanbanGroupKey;
        targetSection: KanbanGroupKey;
        targetSubsection: KanbanGroupKey;
        updatedData: Record<string, unknown>;
      }>,
    ) => {
      const {
        blockId,
        instanceId,
        sourceSection,
        sourceSubsection,
        targetSection,
        targetSubsection,
        updatedData,
      } = action.payload;

      const getRowFromCollection = (
        section: KanbanGroupKey,
        subsection: KanbanGroupKey,
      ): Record<string, unknown> | undefined => {
        const scope = makeKanbanCollectionScope(blockId, section, subsection);
        const collection = getOrCreateCollection(state, scope);
        return collection.working.find((row) => row.id === instanceId);
      };

      const mergeKanbanRowAfterUpdate = (
        existing: Record<string, unknown> | undefined,
        updated: Record<string, unknown>,
      ): Record<string, unknown> => {
        if (!existing) {
          return updated;
        }

        return {
          ...existing,
          ...updated,
          _meta: existing._meta ?? updated._meta,
        };
      };

      const sourceRow = getRowFromCollection(sourceSection, sourceSubsection);
      const mergedRow = mergeKanbanRowAfterUpdate(sourceRow, updatedData);

      const removeFromCollection = (
        section: KanbanGroupKey,
        subsection: KanbanGroupKey,
      ) => {
        const scope = makeKanbanCollectionScope(blockId, section, subsection);
        const collection = getOrCreateCollection(state, scope);
        collection.working = collection.working.filter(
          (row) => row.id !== instanceId,
        );
        collection.original = collection.original.filter(
          (row) => row.id !== instanceId,
        );
      };

      const addToCollection = (
        section: KanbanGroupKey,
        subsection: KanbanGroupKey,
      ) => {
        const scope = makeKanbanCollectionScope(blockId, section, subsection);
        const collection = getOrCreateCollection(state, scope);
        const alreadyPresent = collection.working.some(
          (row) => row.id === instanceId,
        );
        if (!alreadyPresent) {
          collection.working = [...collection.working, mergedRow];
          collection.original = [...collection.original, mergedRow];
        }
      };

      const adjustCollectionTotal = (
        section: KanbanGroupKey,
        subsection: KanbanGroupKey,
        delta: number,
      ) => {
        const scope = makeKanbanCollectionScope(blockId, section, subsection);
        const collection = getOrCreateCollection(state, scope);
        const prev = collection.ui.pagination ?? {
          page: 1,
          total: 0,
          hasMore: false,
          limit: 10,
        };
        collection.ui = {
          ...collection.ui,
          pagination: {
            ...prev,
            total: Math.max(0, (prev.total ?? 0) + delta),
          },
        };
      };

      if (
        !kanbanGroupKeysEqual(sourceSection, targetSection) ||
        !kanbanGroupKeysEqual(sourceSubsection, targetSubsection)
      ) {
        removeFromCollection(sourceSection, sourceSubsection);
        addToCollection(targetSection, targetSubsection);
        adjustCollectionTotal(sourceSection, sourceSubsection, -1);
        adjustCollectionTotal(targetSection, targetSubsection, 1);
      }
    },
    commitFormFieldValue: (
      state,
      action: PayloadAction<{
        blockId: string;
        fieldHandle: string;
        value: unknown;
      }>,
    ) => {
      const { blockId, fieldHandle, value } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const apply = (rows: Array<Record<string, unknown>>) => {
        const parent = { ...(rows[0] ?? {}) };
        parent[fieldHandle] = value;
        return [parent];
      };
      if (Array.isArray(collection.working) && collection.working.length > 0) {
        collection.working = apply(collection.working);
      }
      if (
        Array.isArray(collection.original) &&
        collection.original.length > 0
      ) {
        collection.original = apply(collection.original);
      } else if (
        Array.isArray(collection.working) &&
        collection.working.length > 0
      ) {
        collection.original = apply(collection.working);
      }

      if (state.data?.blocks) {
        const formBlock = (state.data.blocks as PageBlock[]).find(
          (block) => block.id === blockId,
        );
        if (
          formBlock &&
          Array.isArray(collection.working) &&
          collection.working[0]
        ) {
          formBlock.data = collection.working[0];
        }
      }
    },
    updateSelectionMode: (
      state,
      action: {
        payload: { blockId: string; mode: "manual" | "filter" | undefined };
      },
    ) => {
      const { blockId, mode } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      collection.ui = {
        ...collection.ui,
        selectionMode: mode,
        // Filter mode resolves recipients via query, not accumulated row ids.
        // Clear ids when entering filter mode or fully deselecting.
        ...(mode === "filter" || mode === undefined
          ? { selectedIds: [] as string[] }
          : {}),
      };
    },
    updateSelectedIds: (
      state,
      action: {
        payload: { blockId: string; selectedIds: string[] };
      },
    ) => {
      const { blockId, selectedIds } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const uniqueSelectedIds = Array.from(new Set(selectedIds));
      const hasAnySelected = uniqueSelectedIds.length > 0;
      const nextMode: "manual" | "filter" | undefined = hasAnySelected
        ? collection.ui.selectionMode === "filter"
          ? "filter"
          : "manual"
        : undefined;

      collection.ui = {
        ...collection.ui,
        selectedIds: uniqueSelectedIds,
        selectionMode: nextMode,
      };
    },
    updateWorkingRowState: (
      state,
      action: {
        payload: {
          blockId: string;
          rowId: string;
          patch: {
            errors?: Record<string, { code: string; message: string }> | null;
            saving?: boolean;
            saveError?: boolean;
            label?: string | null;
            isSelected?: boolean;
          };
        };
      },
    ) => {
      const { blockId, rowId, patch } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const currentRows = collection.ui.rows || {};
      const prev = currentRows[rowId];
      const next: RowState = prev ? { ...prev } : {};
      if (patch.errors === null) delete next.errors;
      else if (patch.errors) next.errors = patch.errors;
      if (patch.label === null) delete next.label;
      else if (typeof patch.label !== "undefined") next.label = patch.label;
      if (typeof patch.saving !== "undefined") next.saving = patch.saving;
      if (typeof patch.saveError !== "undefined")
        next.saveError = patch.saveError;
      if (typeof patch.isSelected !== "undefined")
        next.isSelected = patch.isSelected;
      collection.ui = {
        ...collection.ui,
        rows: {
          ...currentRows,
          [rowId]: next,
        },
      };
    },
    updateRowFieldOptions: (
      state,
      action: {
        payload: {
          blockId: string;
          rowId: string;
          fieldHandle: string;
          options: Array<{
            value: string;
            label: string;
            avatar?: { seed?: string };
          }>;
        };
      },
    ) => {
      const { blockId, rowId, fieldHandle, options } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const currentRows = (collection.ui.rows || {}) as Record<
        string,
        RowState | undefined
      >;
      const rowState: RowState = currentRows[rowId] || {};
      const fieldsState: NonNullable<RowState["fields"]> =
        rowState.fields || {};
      const nextRowState = {
        ...rowState,
        fields: {
          ...fieldsState,
          [fieldHandle]: {
            ...(fieldsState[fieldHandle] || {}),
            options,
          },
        },
      };
      const nextRows = {
        ...currentRows,
        [rowId]: nextRowState,
      };
      collection.ui = {
        ...collection.ui,
        rows: nextRows,
      };
    },
    updateFormFieldOptions: (
      state,
      action: PayloadAction<{
        blockId: string;
        fieldHandle: string;
        options: { value: string; label: string }[];
      }>,
    ) => {
      const { blockId, fieldHandle, options } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const currentFormUi = collection.ui.form || {};
      collection.ui = {
        ...collection.ui,
        form: {
          ...currentFormUi,
          [fieldHandle]: {
            ...(currentFormUi[fieldHandle] || {}),
            options,
          },
        },
      };
    },
    clearWorkingRowFieldError: (
      state,
      action: {
        payload: { blockId: string; rowId: string; fieldHandle: string };
      },
    ) => {
      const { blockId, rowId, fieldHandle } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const currentRows = (collection.ui.rows || {}) as Record<
        string,
        RowState | undefined
      >;
      const rowState = currentRows[rowId];
      if (!rowState) return;
      const nextErrors = { ...(rowState.errors || {}) };
      delete nextErrors[fieldHandle];
      const hasAny = Object.keys(nextErrors).length > 0;
      const next: RowState = { ...rowState };
      if (hasAny) next.errors = nextErrors;
      else delete next.errors;
      collection.ui = {
        ...collection.ui,
        rows: {
          ...currentRows,
          [rowId]: next,
        },
      };
    },
    clearFieldValidationError: (
      state,
      action: PayloadAction<{ fieldHandle: string }>,
    ) => {
      const { fieldHandle } = action.payload;
      if (!state.data?.fields) return;
      state.data.fields.forEach((field: Field) => {
        if (field.handle === fieldHandle) {
          field.error = undefined;
        }
      });
    },
    // Update page data (default block + consolidated) active_workflows chips for instance pages
    updateActiveWorkflowsForInstance: (
      state,
      action: PayloadAction<{
        instanceId: string;
        chips: Array<Record<string, unknown>>;
      }>,
    ) => {
      const { chips } = action.payload;
      const page = state.data;
      if (!page || page.type === "LIST") return;
      // Update main/default block data if present and object
      const mainModelId = page.modelId;
      const defaultBlock = (page.blocks as PageBlock[]).find(
        (b) => b.modelId === mainModelId && b.default,
      );
      if (
        defaultBlock &&
        defaultBlock.data &&
        typeof defaultBlock.data === "object" &&
        !Array.isArray(defaultBlock.data)
      ) {
        defaultBlock.data["active_workflows"] = chips;
      }
      // Update consolidatedData if it is an object
      if (
        page.consolidatedData &&
        typeof page.consolidatedData === "object" &&
        !Array.isArray(page.consolidatedData)
      ) {
        (page.consolidatedData as Record<string, unknown>)["active_workflows"] =
          chips;
      }
    },
    removeWorkingRowStateById: (
      state,
      action: { payload: { blockId: string; rowId: string } },
    ) => {
      const { blockId, rowId } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const collectionRows = collection.ui.rows || {};
      if (collectionRows[rowId]) {
        const updatedRows: Record<string, unknown> = {
          ...collectionRows,
        };
        delete updatedRows[rowId];
        collection.ui = {
          ...collection.ui,
          rows: updatedRows,
        };
      }
    },
    resetWorkingBlockData: (
      state,
      action: PayloadAction<{ blockId: string }>,
    ) => {
      const { blockId } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      collection.working = collection.original ? [...collection.original] : [];
    },

    updateTeamChatScope: (state, action: PayloadAction<string>) => {
      state.teamChat.currentScope = action.payload;
    },
    updateTeamChatContextTags: (state, action: PayloadAction<string[]>) => {
      state.teamChat.contextTags = action.payload;
    },
    updateTeamChatActiveSegment: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.teamChat.activeSegmentId = action.payload;
    },
    updateTeamChatActiveTab: (state, action: PayloadAction<string>) => {
      state.teamChat.activeTab = action.payload;
    },
    updateTeamChatProgressMessage: (
      state,
      action: PayloadAction<{ id: string; content: string }>,
    ) => {
      const { id, content } = action.payload;
      const progressMessage = {
        id,
        type: "progress" as const,
        content,
        timestamp: new Date().toISOString(),
      };
      state.teamChat.messages.push(progressMessage);
    },
    updateTeamChatStreamingMessage: (
      state,
      action: PayloadAction<{ id: string; content: string }>,
    ) => {
      const { id, content } = action.payload;

      // Find existing AI message or create new one
      const existingMessageIndex = state.teamChat.messages.findIndex(
        (msg) => msg.id === id && msg.type === "ai",
      );

      if (existingMessageIndex !== -1) {
        // Update existing message
        const existingMessage = state.teamChat.messages[existingMessageIndex];
        if (existingMessage) {
          existingMessage.content = content;
        }
      } else {
        // Create new AI message
        const aiMessage = {
          id,
          type: "ai" as const,
          content,
          timestamp: new Date().toISOString(),
        };
        state.teamChat.messages.push(aiMessage);
      }
    },
    updateSelectedResources: (
      state,
      action: PayloadAction<{ blockId: string; selectedResources: string[] }>,
    ) => {
      const { blockId, selectedResources } = action.payload;
      if (state.data?.blocks) {
        const blockIndex = state.data.blocks.findIndex(
          (block: Block) => block.id === blockId,
        );
        if (blockIndex !== -1 && state.data.blocks[blockIndex]) {
          const block = state.data.blocks[blockIndex]!;
          const blockConfig = (block as Block).config;
          if (blockConfig && blockConfig.includes !== undefined) {
            blockConfig.includes = selectedResources;

            // If this is a spreadsheet block, also remove columns for deselected models
            if (blockConfig.type === "spreadsheet") {
              const spreadsheetConfig = blockConfig as SpreadsheetConfig;
              const currentColumns = Array.isArray(spreadsheetConfig.columns)
                ? spreadsheetConfig.columns
                : [];
              const updatedColumns = currentColumns.filter(
                (column: { model?: string }) => {
                  // Keep columns that don't have a model (belong to the main block model)
                  if (!column.model) return true;
                  // Keep columns whose model is in the selected resources OR is the page's main model
                  const mainModelHandle = state.data?.models?.[0]?.handle;
                  return (
                    selectedResources.includes(column.model) ||
                    column.model === mainModelHandle
                  );
                },
              );
              spreadsheetConfig.columns = updatedColumns;
            }
          }
        }
      }
    },
    updatePagination: (
      state,
      action: PayloadAction<{
        blockId: string;
        pagination: {
          page: number;
          total: number;
          hasMore: boolean;
          limit: number;
        };
      }>,
    ) => {
      const { blockId, pagination } = action.payload;
      const collection = getDefaultCollectionForBlock(state, blockId);
      collection.ui = {
        ...collection.ui,
        pagination,
      };
    },
    updatePaginationBulk: (
      state,
      action: PayloadAction<
        Array<{
          blockId: string;
          pagination: {
            page: number;
            total: number;
            hasMore: boolean;
            limit: number;
          };
        }>
      >,
    ) => {
      action.payload.forEach(({ blockId, pagination }) => {
        const collection = getDefaultCollectionForBlock(state, blockId);
        collection.ui = {
          ...collection.ui,
          pagination,
        };
      });
    },
    // Layout draft reducers for managing uncommitted layout changes
    setLayoutDraft: (
      state,
      action: PayloadAction<{
        pageId: string;
        blockId: string;
        config: z.infer<typeof BlockConfigSchema>;
      }>,
    ) => {
      const { pageId, blockId, config } = action.payload;
      // Find the original config from the current page data
      const originalConfig =
        state.data?.blocks?.find((b: Block) => b.id === blockId)?.config ??
        null;

      if (!state.layoutDraft) {
        // Create new draft
        state.layoutDraft = {
          pageId,
          blockId,
          originalConfig,
          draftConfig: config,
          isDirty: true,
        };
      } else if (state.layoutDraft.blockId === blockId) {
        // Update existing draft for the same block
        state.layoutDraft.draftConfig = config;
        state.layoutDraft.isDirty = true;
      } else {
        // Different block - replace the draft
        state.layoutDraft = {
          pageId,
          blockId,
          originalConfig,
          draftConfig: config,
          isDirty: true,
        };
      }
    },
    updateLayoutDraft: (
      state,
      action: PayloadAction<Partial<z.infer<typeof BlockConfigSchema>>>,
    ) => {
      if (state.layoutDraft) {
        state.layoutDraft.draftConfig = {
          ...state.layoutDraft.draftConfig,
          ...action.payload,
        };
        state.layoutDraft.isDirty = true;
      }
    },
    clearLayoutDraft: (state) => {
      const draftBlockId = state.layoutDraft?.blockId;
      state.layoutDraft = null;
      if (draftBlockId && state.queryBarUi?.[draftBlockId]?.layout) {
        const { layout: _layout, ...rest } = state.queryBarUi[draftBlockId]!;
        state.queryBarUi[draftBlockId] = rest;
      }
    },
    discardLayoutDraft: (state) => {
      // Revert the block config to original if we have a draft
      if (state.layoutDraft && state.data?.blocks) {
        const blockIndex = state.data.blocks.findIndex(
          (b: Block) => b.id === state.layoutDraft?.blockId,
        );
        if (blockIndex !== -1 && state.data.blocks[blockIndex]) {
          state.data.blocks[blockIndex]!.config =
            state.layoutDraft.originalConfig ?? undefined;
        }
      }
      if (state.layoutDraft && state.data) {
        state.data.baseQuery = state.layoutDraft.originalBaseQuery ?? null;
      }
      const draftBlockId = state.layoutDraft?.blockId;
      state.layoutDraft = null;
      if (draftBlockId && state.queryBarUi?.[draftBlockId]?.layout) {
        const { layout: _layout, ...rest } = state.queryBarUi[draftBlockId]!;
        state.queryBarUi[draftBlockId] = rest;
      }
    },
    applyLayoutDraftToUI: (state) => {
      applyLayoutDraftToPageData(state);
    },
    setLayoutQueryDraft: (
      state,
      action: PayloadAction<{
        pageId: string;
        blockId: string;
        baseQuery: GetPageResponse["baseQuery"];
      }>,
    ) => {
      const { pageId, blockId, baseQuery } = action.payload;
      const originalConfig =
        state.data?.blocks?.find((b: Block) => b.id === blockId)?.config ??
        null;
      const originalBaseQuery = state.data?.baseQuery ?? null;

      if (!state.layoutDraft) {
        state.layoutDraft = {
          pageId,
          blockId,
          originalConfig,
          draftConfig:
            originalConfig ?? ({} as z.infer<typeof BlockConfigSchema>),
          originalBaseQuery,
          draftBaseQuery: baseQuery,
          isDirty: true,
        };
      } else if (state.layoutDraft.blockId === blockId) {
        state.layoutDraft.draftBaseQuery = baseQuery;
        state.layoutDraft.isDirty = true;
      } else {
        state.layoutDraft = {
          pageId,
          blockId,
          originalConfig,
          draftConfig:
            originalConfig ?? ({} as z.infer<typeof BlockConfigSchema>),
          originalBaseQuery,
          draftBaseQuery: baseQuery,
          isDirty: true,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch Page
    builder
      .addCase(fetchPage.pending, (state, action) => {
        if (state.notFoundPath !== action.meta.arg.path) {
          state.notFoundPath = null;
        }
        if (action.meta.arg.dataOnly) {
          const blockId =
            state.data?.blocks?.find((block) => block.default)?.id ??
            state.data?.blocks?.[0]?.id;
          if (blockId) {
            const collection = getDefaultCollectionForBlock(state, blockId);
            collection.ui = {
              ...collection.ui,
              isFetching: true,
            };
          }
          state.error = null;
          return;
        }

        // Check if we're requesting the same instance we already have
        const currentData = state.data;
        if (currentData && currentData.instance?.data?.id) {
          const currentInstanceId = currentData.instance.data.id;
          const pathParts = action.meta.arg.path.split("/");
          const requestedInstanceId = pathParts[pathParts.length - 1];

          // If it's the same instance, don't set loading state
          if (currentInstanceId === requestedInstanceId) {
            return; // Skip setting loading state
          }
        }

        // Set loading state for actual network requests
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPage.fulfilled, (state, action) => {
        if (state.notFoundPath === action.meta.arg.path) {
          state.notFoundPath = null;
        }
        const dataOnly = action.meta.arg.dataOnly === true;
        const view = action.meta.arg.view as ViewType | undefined;
        const kanbanQueryKey = action.meta.arg.kanbanQueryKey;
        const calendarWindowKey = action.meta.arg.calendarWindowKey;

        if (dataOnly && state.data && action.payload?.type === "LIST") {
          state.isLoading = false;
          const payload = action.payload as GetPageResponse;
          const defaultBlockBefore =
            state.data.blocks?.find((block) => block.default) ??
            state.data.blocks?.[0];
          const previousCollection =
            view === "calendar" && defaultBlockBefore?.id
              ? getDefaultCollectionForBlock(state, defaultBlockBefore.id)
              : null;
          const previousWorking = previousCollection?.working ?? [];
          const previousOriginal = previousCollection?.original ?? [];
          for (const block of payload.blocks ?? []) {
            const idx =
              state.data.blocks?.findIndex(
                (existing) => existing.id === block.id,
              ) ?? -1;
            if (idx >= 0 && state.data.blocks?.[idx]) {
              state.data.blocks[idx] = {
                ...state.data.blocks[idx],
                data: block.data,
                pagination:
                  block.pagination ?? state.data.blocks[idx].pagination,
              };
            }
          }
          hydrateListPageCollections(state, state.data, view);
          if (view === "calendar") {
            const defaultBlock =
              state.data.blocks?.find((block) => block.default) ??
              state.data.blocks?.[0];
            if (defaultBlock?.id) {
              const collection = getDefaultCollectionForBlock(
                state,
                defaultBlock.id,
              );
              const window = action.meta.arg.calendarWindow;
              const merged = mergeCalendarWindowRows({
                previousWorking,
                previousOriginal,
                serverRows: Array.isArray(collection.working)
                  ? collection.working
                  : [],
                startHandle: window?.sessionIdentifiers?.start,
                endHandle: window?.sessionIdentifiers?.end,
                startAt: window?.startAt,
                endAt: window?.endAt,
                alwaysKeepIds: state.recentlyCreatedInstanceIds,
              });
              collection.working = merged.working;
              collection.original = merged.original;
              if (Array.isArray(defaultBlock.data)) {
                defaultBlock.data = merged.original;
              }
            }
            state.calendarEventInstanceById = {};
          }
          const defaultBlock =
            state.data.blocks?.find((block) => block.default) ??
            state.data.blocks?.[0];
          if (
            typeof kanbanQueryKey === "string" &&
            kanbanQueryKey.length > 0 &&
            defaultBlock?.id
          ) {
            markKanbanQueryLoaded(defaultBlock.id, kanbanQueryKey);
          }
          if (
            typeof calendarWindowKey === "string" &&
            calendarWindowKey.length > 0 &&
            defaultBlock?.id
          ) {
            markCalendarWindowLoaded(defaultBlock.id, calendarWindowKey);
          }
          return;
        }

        state.isLoading = false;
        state.recentlyCreatedInstanceIds = [];
        const keepLayoutDraft =
          Boolean(state.layoutDraft) &&
          state.data?.id != null &&
          action.payload?.id === state.data.id;

        if (!keepLayoutDraft) {
          state.layoutDraft = null;
        }

        state.data = action.payload;

        // Store block data in both original and working copies
        if (action.payload && action.payload.blocks && state.data) {
          const payload = action.payload as GetPageResponse;
          if (payload.type === "LIST") {
            hydrateListPageCollections(state, payload, view);
            if (view === "calendar") {
              state.calendarEventInstanceById = {};
            }
            const defaultBlock =
              payload.blocks?.find((block) => block.default) ??
              payload.blocks?.[0];
            if (
              typeof kanbanQueryKey === "string" &&
              kanbanQueryKey.length > 0 &&
              defaultBlock?.id
            ) {
              markKanbanQueryLoaded(defaultBlock.id, kanbanQueryKey);
            }
            if (
              typeof calendarWindowKey === "string" &&
              calendarWindowKey.length > 0 &&
              defaultBlock?.id
            ) {
              markCalendarWindowLoaded(defaultBlock.id, calendarWindowKey);
            }
          } else {
            const mainModelId = payload.modelId;

            (
              payload.blocks as Array<
                PageBlock & {
                  relationshipLink?: {
                    id?: string;
                    cardinality?: string;
                  } | null;
                }
              >
            ).forEach((block) => {
              const data = block.data;
              if (data === undefined) return;

              const link = block.relationshipLink;

              // Default mapping is identity
              let mapData: Record<string, unknown> | Record<string, unknown>[] =
                data;

              if (
                block.modelId !== mainModelId &&
                link &&
                link.cardinality !== "ONE_TO_MANY"
              ) {
                // Attempt to flatten nested object under source handle if present
                const sourceField = (
                  payload.fields as (Field & {
                    relationshipLink?: { id?: string } | null;
                  })[]
                ).find(
                  (f) =>
                    f.modelId === mainModelId &&
                    f.relationshipLink?.id === link.id,
                );
                const sourceHandle = sourceField?.handle;
                if (
                  sourceHandle &&
                  data &&
                  typeof data === "object" &&
                  !Array.isArray(data) &&
                  sourceHandle in data
                ) {
                  const candidate = data[sourceHandle];
                  if (isArrayOfRecords(candidate) || isRecord(candidate)) {
                    mapData = candidate;
                  }
                }
              }

              // Normalize into an array for the unified collection model
              const normalizedArray: Array<Record<string, unknown>> =
                isArrayOfRecords(mapData)
                  ? mapData
                  : isRecord(mapData)
                    ? [mapData]
                    : [];

              const defaultCollectionScope: BlockCollectionScope = {
                blockId: block.id,
                view: "spreadsheet",
              };
              const collection = getOrCreateCollection(
                state,
                defaultCollectionScope,
              );

              collection.original = normalizedArray;
              collection.working = normalizedArray;
              if (block.pagination) {
                collection.ui = {
                  ...collection.ui,
                  pagination: {
                    page: block.pagination.page,
                    total: block.pagination.total,
                    hasMore: block.pagination.hasMore,
                    limit: block.pagination.limit,
                  },
                };
              }
            });
          }
        } else {
          // Reset collections if no blocks
          state.blockData.collections = {};
        }

        // Initialize teamChat state based on page data
        const isNewRecord = !action.payload || action.payload.type === "LIST";
        state.teamChat.currentScope = "agent";
        state.teamChat.contextTags = isNewRecord
          ? ["New Customer"]
          : ["Existing Customer"];
        state.teamChat.isTyping = false;

        if (keepLayoutDraft) {
          applyLayoutDraftToPageData(state);
        }
      })
      .addCase(deleteInstances.fulfilled, (state, action) => {
        const arg = action.meta.arg;
        const targetModelId = arg.modelId;

        if (!state.data?.blocks) return;

        // Filter mode: remove everything in blocks for the model
        if (arg.selectionMode === "filter") {
          state.data.blocks.forEach((block) => {
            if (!block?.id || block.modelId !== targetModelId) return;
            const blockId = block.id;

            const collection = getDefaultCollectionForBlock(state, blockId);
            // Clear all rows for this block
            collection.working = [];
            collection.original = [];
            // Reset selection state and row UI state
            collection.ui = {
              ...collection.ui,
              rows: {},
              selectedIds: [],
              selectionMode: undefined,
              pagination: {
                page: 1,
                limit: collection.ui.pagination?.limit ?? 50,
                total: 0,
                hasMore: false,
              },
            };

            // Keep page blocks in sync if present
            if (Array.isArray(block.data)) {
              block.data = [];
            }
            block.pagination = collection.ui.pagination;
          });
          return;
        }

        // Manual mode: remove specific ids
        const deletedIds = new Set((arg.ids || []).map(String));
        if (deletedIds.size === 0) return;

        state.data.blocks.forEach((block) => {
          if (!block?.id || block.modelId !== targetModelId) return;

          const blockId = block.id;
          const collection = getDefaultCollectionForBlock(state, blockId);
          const working = Array.isArray(collection.working)
            ? collection.working
            : [];

          if (working.length > 0) {
            const filtered = working.filter(
              (r) => !deletedIds.has(String((r as { id?: unknown }).id)),
            );
            collection.working = filtered;
            collection.original = filtered;

            // Reflect into page blocks if present
            if (Array.isArray(block.data)) {
              block.data = filtered;
            }
          }

          // Clear per-row UI state and selection for deleted rows
          const currentRows = collection.ui.rows || {};
          const nextRows: Record<string, unknown> = {};
          Object.entries(currentRows).forEach(([rid, val]) => {
            if (!deletedIds.has(rid)) nextRows[rid] = val;
          });
          const currentSelected = collection.ui.selectedIds || [];
          const nextSelected = currentSelected.filter(
            (rid) => !deletedIds.has(String(rid)),
          );

          collection.ui = {
            ...collection.ui,
            rows: nextRows,
            selectedIds: nextSelected,
            selectionMode:
              nextSelected.length === 0
                ? undefined
                : collection.ui.selectionMode,
          };
        });
      })
      .addCase(fetchPage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        if (action.payload === PAGE_NOT_FOUND_ERROR) {
          state.notFoundPath = action.meta.arg.path;
        }
      })
      .addCase(fetchInstancePageStructure.pending, (state, action) => {
        const modelId = action.meta.arg.modelId;
        state.instancePageStructureByModelId[modelId] = { status: "loading" };
      })
      .addCase(fetchInstancePageStructure.fulfilled, (state, action) => {
        const { modelId, structure } = action.payload;
        state.instancePageStructureByModelId[modelId] = {
          status: "loaded",
          structure,
        };
      })
      .addCase(fetchInstancePageStructure.rejected, (state, action) => {
        const modelId = action.meta.arg.modelId;
        state.instancePageStructureByModelId[modelId] = { status: "error" };
      })
      .addCase(fetchCalendarEventInstance.pending, (state, action) => {
        const instanceId = action.meta.arg.instanceId;
        state.calendarEventInstanceById[instanceId] = { status: "loading" };
      })
      .addCase(fetchCalendarEventInstance.fulfilled, (state, action) => {
        const { instanceId, calendarBlockId, row } = action.payload;
        mergeCalendarEventInstanceRow(state, calendarBlockId, instanceId, row);
        state.calendarEventInstanceById[instanceId] = { status: "ready" };
      })
      .addCase(fetchCalendarEventInstance.rejected, (state, action) => {
        const instanceId = action.meta.arg.instanceId;
        state.calendarEventInstanceById[instanceId] = { status: "error" };
      });

    // Validate Field
    builder
      .addCase(validateField.pending, (state, action) => {
        const { fieldId } = action.meta.arg;

        // Set loading state on the field
        if (state.data?.fields) {
          state.data.fields.forEach((field: Field) => {
            if (field.id === fieldId) {
              field.isLoading = true;
              field.error = undefined; // Clear any previous error
            }
          });
        }
      })
      .addCase(validateField.fulfilled, (state, action) => {
        const { fieldId, isValid, error } = action.payload;

        // Find and update the field in the page data
        if (state.data?.fields) {
          state.data.fields.forEach((field: Field) => {
            if (field.id === fieldId) {
              // Set loading to false
              field.isLoading = false;
              // Set error or clear it based on validation result
              if (isValid) {
                field.error = undefined;
              } else {
                // Map validation error codes to field error codes
                const fieldErrorCode =
                  error?.code === "INVALID_OPTION"
                    ? "REGEX"
                    : error?.code === "UNKNOWN"
                      ? "REQUIRED"
                      : (error?.code as "REQUIRED" | "REGEX" | "UNIQUE");
                field.error = error
                  ? { code: fieldErrorCode, message: error.message }
                  : { code: "REQUIRED", message: "Validation failed" };
              }
            }
          });
        }
      })
      .addCase(validateField.rejected, (state, action) => {
        // Handle validation error - could set a general error or specific field error
        const errorPayload = action.payload as
          | { code: string; message: string }
          | string;
        state.error =
          typeof errorPayload === "string"
            ? errorPayload
            : errorPayload.message;

        // Set loading to false and error on the field
        const { fieldId } = action.meta.arg;
        if (state.data?.fields) {
          state.data.fields.forEach((field: Field) => {
            if (field.id === fieldId) {
              field.isLoading = false;
              field.error = {
                code: "REQUIRED",
                message: "Validation failed",
              };
            }
          });
        }
      });

    // Update Query Bar
    builder
      .addCase(updateQueryBar.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;

        const { blockId, filters, sort } = action.meta.arg;
        state.blockData.filters = state.blockData.filters ?? {};
        state.blockData.filters[blockId] = filters;
        if (sort) {
          state.blockData.sort = state.blockData.sort ?? {};
          state.blockData.sort[blockId] = sort;
        } else if (state.blockData.sort?.[blockId]) {
          const nextSort = { ...state.blockData.sort };
          delete nextSort[blockId];
          state.blockData.sort = nextSort;
        }

        // find the block with the id
        const block = state.data?.blocks.find(
          (block: Block) => block.id === blockId,
        );
        if (!block) {
          return;
        }

        // If we were in filter selection mode, clear selection on new searches
        const collection = getDefaultCollectionForBlock(state, blockId);
        if (collection.ui.selectionMode === "filter") {
          collection.ui = {
            ...collection.ui,
            selectionMode: undefined,
            selectedIds: [],
          };
        }
      })
      .addCase(updateQueryBar.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateQueryBar.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch relation options per row/field (spreadsheet)
    builder.addCase(fetchRelationOptions.fulfilled, (state, action) => {
      const { blockId, rowId, fieldHandle, options, cache } =
        action.payload as {
          blockId: string;
          rowId: string;
          fieldHandle: string;
          options: Array<{
            value: string;
            label: string;
            avatar?: { seed?: string };
          }>;
          cache?: boolean;
        };
      if (cache === false) return;
      const collection = getDefaultCollectionForBlock(state, blockId);
      const currentRows = (collection.ui.rows || {}) as Record<
        string,
        RowState | undefined
      >;
      const rowState: RowState = currentRows[rowId] || {};
      const fieldsState: NonNullable<RowState["fields"]> =
        rowState.fields || {};
      const nextRowState: RowState = {
        ...rowState,
        fields: {
          ...fieldsState,
          [fieldHandle]: {
            ...(fieldsState[fieldHandle] || {}),
            options,
          },
        },
      };
      const nextRows = {
        ...currentRows,
        [rowId]: nextRowState,
      };
      collection.ui = {
        ...collection.ui,
        rows: nextRows,
      };
    });

    // Load More Block Items
    builder
      .addCase(loadMoreBlockItems.pending, (state, action) => {
        const scope: BlockCollectionScope = {
          blockId: action.meta.arg.blockId,
          view: "spreadsheet",
        };
        const collection = getOrCreateCollection(state, scope);
        collection.ui = {
          ...collection.ui,
          isFetching: true,
        };
      })

      .addCase(loadMoreBlockItems.fulfilled, (state, action) => {
        const {
          blockId,
          newData,
          pagination,
          requestedPage,
          requestedLimit,
          replace,
        } = action.payload as {
          blockId: string;
          newData: Array<Record<string, unknown>>;
          pagination?: {
            page?: number;
            total?: number;
            hasMore?: boolean;
            limit?: number;
          };
          requestedPage?: number;
          requestedLimit?: number;
          replace?: boolean;
        };

        const scope: BlockCollectionScope = {
          blockId,
          view: "spreadsheet",
        };
        const collection = getOrCreateCollection(state, scope);
        const existingArray = Array.isArray(collection.working)
          ? collection.working
          : [];

        // Replace data if replace flag is true (e.g., when filters change)
        // Otherwise, concatenate for pagination (next page)
        const finalData = replace
          ? newData || []
          : existingArray.concat(newData || []);

        collection.working = finalData;
        collection.original = finalData;

        // Compute effective pagination, advancing page/limit if server omitted them
        const previousPagination = collection.ui.pagination;
        const effectivePagination: {
          page: number;
          total: number;
          hasMore: boolean;
          limit: number;
        } = {
          page: 1,
          total: 0,
          hasMore: false,
          limit: 50,
          ...(previousPagination || {}),
          ...(pagination || {}),
        };
        if (typeof effectivePagination.page !== "number" && requestedPage) {
          effectivePagination.page = requestedPage;
        } else if (
          requestedPage &&
          typeof effectivePagination.page === "number" &&
          effectivePagination.page < requestedPage
        ) {
          effectivePagination.page = requestedPage;
        }
        if (typeof effectivePagination.limit !== "number" && requestedLimit) {
          effectivePagination.limit = requestedLimit;
        }
        if (typeof effectivePagination.hasMore !== "boolean") {
          const limitForHasMore =
            effectivePagination.limit || requestedLimit || 50;
          effectivePagination.hasMore =
            (newData || []).length >= limitForHasMore;
        }

        // Reflect into page blocks if present
        if (state.data?.blocks) {
          const idx = state.data.blocks.findIndex(
            (b) => (b as unknown as PageBlock)?.id === blockId,
          );
          if (idx !== -1 && state.data.blocks[idx]) {
            const blk = state.data.blocks[idx] as unknown as PageBlock;
            if (Array.isArray(blk.data)) {
              blk.data = finalData;
            }
            blk.pagination = effectivePagination;
          }
        }

        // Reflect pagination and loading flags into the unified collection UI
        collection.ui = {
          ...collection.ui,
          isFetching: false,
          pagination: effectivePagination,
        };
      })
      // Kanban subsection loading
      .addCase(loadKanbanSubsectionItems.pending, (state, action) => {
        const { blockId, section, subsection, page } = action.meta.arg;

        const scope = makeKanbanCollectionScope(blockId, section, subsection);
        const collection = getOrCreateCollection(state, scope);
        if (page === 1) {
          collection.original = [];
          collection.working = [];
        }
        collection.ui = {
          ...collection.ui,
          isFetching: true,
        };
      })
      .addCase(loadKanbanSubsectionItems.fulfilled, (state, action) => {
        const { blockId, section, subsection, newData, pagination } =
          action.payload as {
            blockId: string;
            section: KanbanGroupKey;
            subsection: KanbanGroupKey;
            newData: Array<Record<string, unknown>>;
            pagination?: {
              page?: number;
              total?: number;
              hasMore?: boolean;
              limit?: number;
            };
          };
        const scope = makeKanbanCollectionScope(blockId, section, subsection);
        const collection = getOrCreateCollection(state, scope);
        const existingArray = Array.isArray(collection.working)
          ? collection.working
          : [];
        const appended = existingArray.concat(newData || []);
        collection.working = appended;
        collection.original = appended;

        const prev = collection.ui.pagination;
        const requestedPage = action.meta.arg.page;
        const effective: {
          page: number;
          total: number;
          hasMore: boolean;
          limit: number;
        } = {
          page: 1,
          total: 0,
          hasMore: false,
          limit: 10,
          ...(prev || {}),
          ...(pagination || {}),
        } as unknown as {
          page: number;
          total: number;
          hasMore: boolean;
          limit: number;
        };
        // Update page number if server didn't provide it
        if (typeof effective.page !== "number" && requestedPage) {
          effective.page = requestedPage;
        } else if (
          requestedPage &&
          typeof effective.page === "number" &&
          effective.page < requestedPage
        ) {
          effective.page = requestedPage;
        }
        // Compute hasMore if not provided by server
        // If we got exactly the limit number of items, there might be more
        // If we got fewer, there are definitely no more
        if (typeof effective.hasMore !== "boolean") {
          const limitForHasMore = effective.limit || 10;
          const returnedCount = (newData || []).length;
          effective.hasMore = returnedCount === limitForHasMore;
        }
        collection.ui = {
          ...collection.ui,
          isFetching: false,
          pagination: effective,
        };
      })
      .addCase(loadKanbanSubsectionItems.rejected, (state, action) => {
        const { blockId, section, subsection } = action.meta.arg as {
          blockId: string;
          section: KanbanGroupKey;
          subsection: KanbanGroupKey;
        };
        const scope = makeKanbanCollectionScope(blockId, section, subsection);
        const collection = getOrCreateCollection(state, scope);
        collection.ui = {
          ...collection.ui,
          isFetching: false,
        };
      })
      // Kanban subsection bulk loading
      .addCase(loadKanbanSubsectionItemsBulk.pending, (state, action) => {
        action.meta.arg.forEach(({ blockId, section, subsection }) => {
          const scope = makeKanbanCollectionScope(blockId, section, subsection);
          const collection = getOrCreateCollection(state, scope);
          collection.original = [];
          collection.working = [];
          collection.ui = {
            ...collection.ui,
            isFetching: true,
          };
        });
      })
      .addCase(loadKanbanSubsectionItemsBulk.fulfilled, (state, action) => {
        // Process all results
        action.payload.forEach((result) => {
          const { blockId, section, subsection, newData, pagination } = result;
          const scope = makeKanbanCollectionScope(blockId, section, subsection);
          const collection = getOrCreateCollection(state, scope);
          const existingArray = Array.isArray(collection.working)
            ? collection.working
            : [];
          const appended = existingArray.concat(newData || []);
          collection.working = appended;
          collection.original = appended;

          const prev = collection.ui.pagination;
          const effective: {
            page: number;
            total: number;
            hasMore: boolean;
            limit: number;
          } = {
            page: 1,
            total: 0,
            hasMore: false,
            limit: 10,
            ...(prev || {}),
            ...(pagination || {}),
          } as unknown as {
            page: number;
            total: number;
            hasMore: boolean;
            limit: number;
          };
          // Compute hasMore if not provided by server
          if (typeof effective.hasMore !== "boolean") {
            const limitForHasMore = effective.limit || 10;
            const returnedCount = (newData || []).length;
            effective.hasMore = returnedCount === limitForHasMore;
          }
          collection.ui = {
            ...collection.ui,
            isFetching: false,
            pagination: effective,
          };
        });
      })
      .addCase(loadKanbanSubsectionItemsBulk.rejected, (state, action) => {
        action.meta.arg.forEach(({ blockId, section, subsection }) => {
          const scope = makeKanbanCollectionScope(blockId, section, subsection);
          const collection = getOrCreateCollection(state, scope);
          collection.ui = {
            ...collection.ui,
            isFetching: false,
          };
        });
      })
      .addCase(loadMoreBlockItems.rejected, (state, action) => {
        state.error = action.payload as string;

        const blockId = action.meta.arg.blockId;

        const scope: BlockCollectionScope = {
          blockId,
          view: "spreadsheet",
        };
        const collection = getOrCreateCollection(state, scope);
        collection.ui = {
          ...collection.ui,
          isFetching: false,
        };
      });

    // Send Team Chat Message
    builder
      .addCase(sendTeamChatMessage.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.teamChat.isTyping = true;

        // Add the human message instantly for immediate UI feedback
        const humanMessage = {
          id: Date.now().toString(),
          type: "human" as const,
          content: action.meta.arg,
          timestamp: new Date().toISOString(),
        };
        state.teamChat.messages.push(humanMessage);

        // If activeSegmentId is undefined and we have messages, set it to the segment ID
        if (
          !state.teamChat.activeSegmentId &&
          state.teamChat.messages.length > 0
        ) {
          state.teamChat.activeSegmentId = "1"; // Segment ID for agent conversation
        }
      })
      .addCase(sendTeamChatMessage.fulfilled, (state) => {
        state.isLoading = false;

        // Set typing to false
        state.teamChat.isTyping = false;

        // Note: Progress messages and AI response are now handled in real-time
        // during the streaming process, so we don't need to add them here
      })
      .addCase(sendTeamChatMessage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.teamChat.isTyping = false;
      });

    // Create Instance
    builder
      .addCase(createInstance.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(createInstance.fulfilled, (state, action) => {
        state.isSaving = false;
        // On success, clear any existing field errors in the form
        if (state.data?.fields) {
          state.data.fields.forEach((field: Field) => {
            field.error = undefined;
          });
        }
        // If current page is a LIST, insert the new record into the block data
        if (state.data?.type === "LIST") {
          const payloadUnknown: unknown = action.payload;
          if (
            typeof payloadUnknown === "object" &&
            payloadUnknown !== null &&
            "data" in payloadUnknown
          ) {
            const dataValue = (payloadUnknown as { data: unknown }).data;
            if (
              dataValue &&
              typeof dataValue === "object" &&
              !Array.isArray(dataValue)
            ) {
              const newRecord = dataValue as Record<string, unknown>;
              const createdId =
                typeof newRecord.id === "string" ? newRecord.id : undefined;
              rememberCreatedInstanceId(state, createdId);
              const mainModelId = state.data.modelId;

              (state.data.blocks as PageBlock[]).forEach((block) => {
                if (!block?.id) return;
                if (block.modelId !== mainModelId) return;

                const collection = getDefaultCollectionForBlock(
                  state,
                  block.id,
                );
                const currentWorking = Array.isArray(collection.working)
                  ? collection.working
                  : [];
                const updatedWorking: Array<Record<string, unknown>> = [
                  ...currentWorking,
                  newRecord,
                ];
                collection.working = updatedWorking;
                collection.original =
                  collection.original && collection.original.length > 0
                    ? [...collection.original, newRecord]
                    : updatedWorking;

                if (Array.isArray(block.data)) {
                  block.data = collection.original;
                }
              });

              // Update context tag
              state.teamChat.contextTags = ["New Instance"];
              return;
            }
          }
        }
        // If server returned a full GetPage response, replace page data
        const page = action.payload as GetPageResponse | null;
        if (page && page.blocks && page.fields) {
          state.data = page;
          // Rebuild collections from page.blocks
          state.blockData.collections = {};
          (page.blocks as PageBlock[]).forEach((block) => {
            if (!block?.id) return;
            const scope: BlockCollectionScope = {
              blockId: block.id,
              view: "spreadsheet",
            };
            const collection = getOrCreateCollection(state, scope);
            const rawData = block.data;
            const normalized: Array<Record<string, unknown>> =
              Array.isArray(rawData) && isArrayOfRecords(rawData)
                ? rawData
                : isRecord(rawData)
                  ? [rawData]
                  : [];
            collection.original = normalized;
            collection.working = normalized;
            if (block.pagination) {
              collection.ui = {
                ...collection.ui,
                pagination: {
                  page: block.pagination.page,
                  total: block.pagination.total,
                  hasMore: block.pagination.hasMore,
                  limit: block.pagination.limit,
                },
              };
            }
          });
          // Update context tag
          state.teamChat.contextTags = ["New Instance"];
          return;
        }

        // Fallback: previous normalization (kept for backward compatibility)
        if (
          action.payload &&
          typeof action.payload === "object" &&
          "data" in action.payload &&
          state.data?.blocks
        ) {
          const payloadData =
            (action.payload as { data?: Record<string, unknown> }).data || {};
          const mainModelId = state.data.modelId;

          (state.data.blocks as PageBlock[]).forEach((block) => {
            if (!block?.id) return;

            const link = block.relationshipLink as
              | {
                  id?: string;
                  cardinality?:
                    | "ONE_TO_ONE"
                    | "ONE_TO_MANY"
                    | "MANY_TO_ONE"
                    | string;
                }
              | null
              | undefined;

            let displayData:
              | Record<string, unknown>
              | Record<string, unknown>[]
              | null = {};
            let mapData:
              | Record<string, unknown>
              | Record<string, unknown>[]
              | null = {};

            if (block.modelId === mainModelId) {
              if (state.data?.type === "INSTANCE") {
                const createdModelId = (action.meta.arg as CreateInstanceInput)
                  .modelId;
                if (createdModelId !== mainModelId) {
                  return;
                }
              }
              displayData = payloadData;
              mapData = payloadData;
            } else if (link?.id) {
              const sourceField = (
                state.data!.fields as (Field & {
                  relationshipLink?: {
                    id?: string;
                    cardinality?: string;
                  } | null;
                })[]
              ).find(
                (f) =>
                  f.modelId === mainModelId &&
                  f.relationshipLink?.id === link.id,
              );
              const sourceHandle = sourceField?.handle;
              if (sourceHandle) {
                if (link.cardinality === "ONE_TO_MANY") {
                  const arr = payloadData[sourceHandle] as
                    | Record<string, unknown>[]
                    | undefined;
                  const safeArr = Array.isArray(arr) ? arr : [];
                  displayData = safeArr;
                  mapData = safeArr;
                } else {
                  const obj = payloadData[sourceHandle] as
                    | Record<string, unknown>
                    | undefined;
                  const safeObj = obj ?? null;
                  displayData = { [sourceHandle]: safeObj } as Record<
                    string,
                    unknown
                  >;
                  mapData = safeObj;
                }
              }
            }

            const collection = getDefaultCollectionForBlock(state, block.id);
            const normalized: Array<Record<string, unknown>> = isArrayOfRecords(
              mapData,
            )
              ? mapData
              : isRecord(mapData)
                ? [mapData]
                : [];
            collection.working = normalized;
            collection.original = normalized;
            block.data = displayData ?? undefined;
          });

          if (
            state.data &&
            (action.payload as { generatedLabel?: string }).generatedLabel
          ) {
            state.data.title = (action.payload as { generatedLabel?: string })
              .generatedLabel as string;
          }

          // Update breadcrumbs if possible
          if (state.data) {
            const payloadDataObj = (
              action.payload as { data?: { id?: unknown } }
            ).data;
            const gen = (action.payload as { generatedLabel?: string })
              .generatedLabel;
            if (payloadDataObj?.id && gen) {
              const mainModel = state.data.models?.[0];
              if (mainModel) {
                state.data.breadcrumbs = state.data.breadcrumbs.map(
                  (breadcrumb: Breadcrumb) => {
                    if (
                      breadcrumb.modelId === mainModel.id &&
                      breadcrumb.type === "INSTANCE"
                    ) {
                      return {
                        type: breadcrumb.type,
                        label: gen,
                        href: breadcrumb.href.replace(
                          /\/[^\/]+$/,
                          `/${(payloadDataObj as { id: string }).id}`,
                        ),
                        modelId: breadcrumb.modelId,
                      };
                    }
                    return breadcrumb;
                  },
                );
              }
            }
          }

          // Add activity data
          if (
            state.data &&
            (action.payload as { activity?: unknown[] }).activity
          ) {
            if (!state.data.instance) {
              state.data.instance = {
                data:
                  (action.payload as { data?: Record<string, unknown> }).data ||
                  {},
                generatedLabel:
                  (action.payload as { generatedLabel?: string })
                    .generatedLabel || "",
                activity: [],
              };
            }
            if (!state.data.instance.activity) {
              state.data.instance.activity = [];
            }
            state.data.instance.activity.unshift(
              ...((action.payload as { activity?: unknown[] })
                .activity as unknown[]),
            );
          }
          state.teamChat.contextTags = ["New Instance"];
        }
      })
      .addCase(createInstance.rejected, (state, action) => {
        state.isSaving = false;

        // Handle validation errors by mapping them to field-level errors
        if (
          action.payload &&
          typeof action.payload === "object" &&
          !Array.isArray(action.payload)
        ) {
          const validationErrors = action.payload as Record<
            string,
            { code: "REQUIRED" | "REGEX" | "UNIQUE"; message: string }
          >;

          // Clear any existing field errors first
          if (state.data?.fields) {
            state.data.fields.forEach((field: Field) => {
              field.error = undefined;
            });
          }

          // Map validation errors to field errors using dot notation
          Object.entries(validationErrors).forEach(([errorKey, errorValue]) => {
            // Handle nested field errors (e.g., "billing_address.address_1")
            const fieldHandle = errorKey.split(".")[0]; // Get the top-level field handle

            // Find the field in the fields and set its error
            if (state.data?.fields) {
              state.data.fields.forEach((field: Field) => {
                if (field.handle === fieldHandle) {
                  field.error = errorValue;
                }
              });
            }
          });

          // Set a general error message for any non-field-specific errors
          if (validationErrors["_"]) {
            state.error = validationErrors["_"].message;
          } else {
            state.error = "Validation failed. Please check the form fields.";
          }
        } else {
          // Handle non-validation errors (string messages)
          state.error = action.payload as string;
        }
      });

    // Update Instance
    builder
      .addCase(updateInstance.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateInstance.fulfilled, (state, action) => {
        state.isSaving = false;
        // On success, clear any existing field errors in the form
        if (state.data?.fields) {
          state.data.fields.forEach((field: Field) => {
            field.error = undefined;
          });
        }
        // If current page is a LIST, update the existing record within arrays
        if (state.data?.type === "LIST") {
          const payloadUnknown: unknown = action.payload;
          if (
            typeof payloadUnknown === "object" &&
            payloadUnknown !== null &&
            "data" in payloadUnknown
          ) {
            const dataValue = (payloadUnknown as { data: unknown }).data;
            if (
              dataValue &&
              typeof dataValue === "object" &&
              !Array.isArray(dataValue)
            ) {
              const updatedRecord = dataValue as Record<string, unknown>;
              const updatedId = updatedRecord["id"];
              if (
                typeof updatedId === "string" ||
                typeof updatedId === "number"
              ) {
                const mainModelId = state.data.modelId;
                (state.data.blocks as PageBlock[]).forEach((block) => {
                  if (!block?.id) return;
                  if (block.modelId !== mainModelId) return;

                  const collection = getDefaultCollectionForBlock(
                    state,
                    block.id,
                  );
                  const currentWorking = Array.isArray(collection.working)
                    ? collection.working
                    : [];
                  if (currentWorking.length > 0) {
                    const updatedWorkingArray: Array<Record<string, unknown>> =
                      currentWorking.map((row) => {
                        if (
                          row &&
                          typeof row === "object" &&
                          !Array.isArray(row)
                        ) {
                          const rowId = (row as { id?: unknown }).id;
                          if (rowId === updatedId) {
                            return updatedRecord;
                          }
                        }
                        return row;
                      });
                    collection.working = updatedWorkingArray;

                    const previousOriginal = collection.original;
                    if (Array.isArray(previousOriginal)) {
                      const updatedOriginalArray = previousOriginal.map(
                        (row) => {
                          if (
                            row &&
                            typeof row === "object" &&
                            !Array.isArray(row)
                          ) {
                            const rowId = (row as { id?: unknown }).id;
                            if (rowId === updatedId) {
                              return updatedRecord;
                            }
                          }
                          return row;
                        },
                      );
                      collection.original = updatedOriginalArray;
                      if (Array.isArray(block.data)) {
                        block.data = updatedOriginalArray;
                      }
                    } else {
                      collection.original = updatedWorkingArray;
                      if (Array.isArray(block.data)) {
                        block.data = updatedWorkingArray;
                      }
                    }
                  }
                });
                // Update team chat context tags for updated instance
                state.teamChat.contextTags = ["Updated Instance"];
                return;
              }
            }
          }
        }
        // Update only the blocks that are relevant to the updated data
        if (
          action.payload &&
          typeof action.payload === "object" &&
          "data" in action.payload &&
          state.data?.blocks
        ) {
          const payloadData =
            (action.payload as { data?: Record<string, unknown> }).data || {};
          const mainModelId = state.data.modelId;

          (state.data.blocks as PageBlock[]).forEach((block) => {
            if (!block?.id) return;

            const link = block.relationshipLink as
              | {
                  id?: string;
                  cardinality?:
                    | "ONE_TO_ONE"
                    | "ONE_TO_MANY"
                    | "MANY_TO_ONE"
                    | string;
                }
              | null
              | undefined;

            let displayData:
              | Record<string, unknown>
              | Record<string, unknown>[]
              | undefined;
            let mapData:
              | Record<string, unknown>
              | Record<string, unknown>[]
              | undefined;

            if (block.modelId === mainModelId) {
              if (state.data?.type === "INSTANCE") {
                const updatedInstanceId = (
                  action.meta.arg as UpdateInstanceInput
                ).instanceId;
                const collection = getDefaultCollectionForBlock(
                  state,
                  block.id,
                );
                const mainRowId = (
                  collection.working?.[0] as { id?: unknown } | undefined
                )?.id;
                if (
                  typeof mainRowId === "string" &&
                  mainRowId.length > 0 &&
                  updatedInstanceId !== mainRowId
                ) {
                  return;
                }
              }
              // This block is scoped to the main model – update with full payload
              displayData = payloadData;
              mapData = payloadData;
            } else if (link?.id) {
              // Find the source field on the main model that links to this block's model
              const sourceField = (
                state.data!.fields as (Field & {
                  relationshipLink?: {
                    id?: string;
                    cardinality?: string;
                  } | null;
                })[]
              ).find(
                (f) =>
                  f.modelId === mainModelId &&
                  f.relationshipLink?.id === link.id,
              );

              const sourceHandle = sourceField?.handle;
              if (sourceHandle && sourceHandle in payloadData) {
                if (link.cardinality === "ONE_TO_MANY") {
                  const arr = payloadData[sourceHandle] as
                    | Record<string, unknown>[]
                    | undefined;
                  if (typeof arr !== "undefined") {
                    displayData = arr;
                    mapData = arr;
                  }
                } else {
                  const obj = payloadData[sourceHandle] as
                    | Record<string, unknown>
                    | null
                    | undefined;
                  if (typeof obj !== "undefined") {
                    displayData = { [sourceHandle]: obj } as Record<
                      string,
                      unknown
                    >;
                    mapData = obj ?? undefined;
                  }
                }
              }
            }

            if (typeof mapData !== "undefined") {
              const collection = getDefaultCollectionForBlock(state, block.id);
              const normalized: Array<Record<string, unknown>> =
                isArrayOfRecords(mapData)
                  ? mapData
                  : isRecord(mapData)
                    ? [mapData]
                    : [];
              collection.working = normalized;
              collection.original = normalized;
              // Keep block.data in sync when we have a computed display value
              if (typeof displayData !== "undefined") {
                block.data = displayData;
              }
            }
          });
        }
        // Update the page data with the generated label if we have page data
        if (
          state.data &&
          action.payload &&
          typeof action.payload === "object" &&
          "generatedLabel" in action.payload
        ) {
          state.data.title =
            (action.payload as { generatedLabel?: string }).generatedLabel ||
            state.data.title;
        }
        // Add activity data to the page data if available
        if (
          state.data &&
          action.payload &&
          typeof action.payload === "object" &&
          "activity" in action.payload
        ) {
          // Ensure instance data exists
          if (!state.data.instance) {
            state.data.instance = {
              data:
                (action.payload as { data?: Record<string, unknown> }).data ||
                {},
              generatedLabel:
                (action.payload as { generatedLabel?: string })
                  .generatedLabel || "",
              activity: [],
            };
          }

          // Initialize activities array if it doesn't exist
          if (!state.data.instance.activity) {
            state.data.instance.activity = [];
          }
          // Add the new activities to the beginning of the activities array
          state.data.instance.activity.unshift(
            ...((action.payload as unknown as { activity?: unknown[] })
              .activity || []),
          );
        }
        // Update team chat context tags for updated instance
        state.teamChat.contextTags = ["Updated Instance"];
      })
      .addCase(updateInstance.rejected, (state, action) => {
        state.isSaving = false;

        // Handle validation errors by mapping them to field-level errors
        if (
          action.payload &&
          typeof action.payload === "object" &&
          !Array.isArray(action.payload)
        ) {
          const validationErrors = action.payload as Record<
            string,
            { code: "REQUIRED" | "REGEX" | "UNIQUE"; message: string }
          >;

          // Clear any existing field errors first
          if (state.data?.fields) {
            state.data.fields.forEach((field: Field) => {
              field.error = undefined;
            });
          }

          // Map validation errors to field errors using dot notation
          Object.entries(validationErrors).forEach(([errorKey, errorValue]) => {
            // Handle nested field errors (e.g., "billing_address.address_1")
            const fieldHandle = errorKey.split(".")[0]; // Get the top-level field handle

            // Find the field in the fields and set its error
            if (state.data?.fields) {
              state.data.fields.forEach((field: Field) => {
                if (field.handle === fieldHandle) {
                  field.error = errorValue;
                }
              });
            }
          });

          // Set a general error message for any non-field-specific errors
          if (validationErrors["_"]) {
            state.error = validationErrors["_"].message;
          } else {
            state.error = "Validation failed. Please check the form fields.";
          }
        } else {
          // Handle non-validation errors (string messages)
          state.error = action.payload as string;
        }
      });

    // Update Model Label Template
    builder
      .addCase(updateModelLabelTemplate.pending, (state) => {
        state.isTitleLoading = true;
        state.error = null;
      })
      .addCase(updateModelLabelTemplate.fulfilled, (state, action) => {
        state.isTitleLoading = false;
        // The mutation returns UnifiedUIResult<ModelWithIncludes>, not page data
        // The page data will be refreshed separately if needed
        // For now, we just mark the loading as complete
      })
      .addCase(updateModelLabelTemplate.rejected, (state, action) => {
        state.isTitleLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updatePageTitle.pending, (state) => {
        state.isTitleLoading = true;
        state.error = null;
      })
      .addCase(updatePageTitle.fulfilled, (state, action) => {
        state.isTitleLoading = false;
        if (!state.data) return;

        const nextTitle = action.payload.title;
        state.data.title = nextTitle;
        state.data.breadcrumbs = state.data.breadcrumbs.map(
          (breadcrumb, index, breadcrumbs) => {
            const isLast = index === breadcrumbs.length - 1;
            if (isLast && breadcrumb.type === "LIST") {
              return {
                ...breadcrumb,
                label: nextTitle,
              };
            }
            return breadcrumb;
          },
        );
      })
      .addCase(updatePageTitle.rejected, (state, action) => {
        state.isTitleLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(changeListPageModel.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changeListPageModel.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(changeListPageModel.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Block Config
    builder
      .addCase(updateBlockConfig.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        // Update the block config immediately for smooth UX
        const { blockId, config } = action.meta.arg;
        if (state.data?.blocks) {
          const blockIndex = state.data.blocks.findIndex(
            (block: Block) => block.id === blockId,
          );
          if (blockIndex !== -1 && state.data.blocks[blockIndex]) {
            state.data.blocks[blockIndex]!.config = config;
          }
        }
      })
      .addCase(updateBlockConfig.fulfilled, (state, _action) => {
        state.isLoading = false;
        // After server confirms the new config (or view switch), request a fresh getPage
        // We do not have direct dispatch here, so set a flag the UI can detect if needed
        // Alternatively, rely on explicit dispatch from the thunk caller (preferred)
      })
      .addCase(updateBlockConfig.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;

        // TODO: Optionally revert the optimistic update on error
        // For now, we'll keep the optimistic update even if the request fails
      });

    // Associate Contact
    builder
      .addCase(associateContact.fulfilled, (state, action) => {
        const contact = (action.payload as { contact?: unknown }).contact;

        // Safely set contact on the current instance block if applicable
        if (state.data?.blocks) {
          const arg = (action.meta?.arg || {}) as { blockId?: string };
          const maybeBlockId: string | undefined = arg.blockId;
          const blockIndex = state.data.blocks.findIndex((block: Block) => {
            if (maybeBlockId) return block.id === maybeBlockId;
            // Fallback: match the first block with the page main model id
            return block.modelId === state.data?.modelId;
          });
          if (blockIndex !== -1 && state.data.blocks[blockIndex]) {
            (
              state.data.blocks[blockIndex] as unknown as Record<
                string,
                unknown
              >
            ).contact = contact;
          }
        }
      })
      .addCase(associateContact.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  clearPageError,
  syncPagePresentationLocally,
  clearPageData,
  invalidateKanbanCollections,
  syncKanbanCollectionsFromBlockData,
  syncBlockQueryCache,
  setPageData,
  updateWorkingBlockData,
  commitFormFieldValue,
  updateSelectionMode,
  updateSelectedIds,
  updateWorkingRowState,
  clearWorkingRowFieldError,
  clearFieldValidationError,
  removeWorkingRowStateById,
  resetWorkingBlockData,
  updateTeamChatScope,
  updateTeamChatContextTags,
  updateTeamChatActiveSegment,
  updateTeamChatActiveTab,
  updateTeamChatProgressMessage,
  updateTeamChatStreamingMessage,
  updateSelectedResources,
  setImportLoading,
  updatePagination,
  updatePaginationBulk,
  updateQueryBarUi,
  syncKanbanItemMove,
  setLayoutDraft,
  updateLayoutDraft,
  clearLayoutDraft,
  discardLayoutDraft,
  applyLayoutDraftToUI,
  setLayoutQueryDraft,
} = pageV2Slice.actions;

// Export selectors
export const selectPageV2 = (state: RootState) => state.pageV2;
export const selectPageV2Data = (state: RootState) => state.pageV2.data;
export const selectTeamChat = (state: RootState) => state.pageV2.teamChat;
export const selectLayoutDraft = (state: RootState) => state.pageV2.layoutDraft;
export const selectHasLayoutDraft = (state: RootState) =>
  state.pageV2.layoutDraft !== null && state.pageV2.layoutDraft.isDirty;
export const selectPageV2IsLoading = (state: RootState) =>
  state.pageV2.isLoading;
export const selectPageV2IsSaving = (state: RootState) => state.pageV2.isSaving;
export const selectPageV2IsTitleLoading = (state: RootState) =>
  state.pageV2.isTitleLoading;
export const selectPageV2Error = (state: RootState) => state.pageV2.error;
export const selectPageV2NotFoundPath = (state: RootState) =>
  state.pageV2.notFoundPath;
export const selectBlockData = (state: RootState) => state.pageV2.blockData;

// Collection-based selectors
export const selectBlockCollections = (state: RootState) =>
  state.pageV2.blockData.collections;

export const selectBlockCollectionForBlock =
  (blockId: string) => (state: RootState) => {
    const collections = state.pageV2.blockData.collections;
    // Prefer the primary "spreadsheet" view collection for this block; fall
    // back to any other collection for this block (e.g., kanban/calendar) if
    // needed.
    const byId =
      Object.values(collections).find(
        (c) => c.scope.blockId === blockId && c.scope.view === "spreadsheet",
      ) ?? Object.values(collections).find((c) => c.scope.blockId === blockId);
    return byId ?? null;
  };

export const selectBlockDataForBlock =
  (blockId: string) => (state: RootState) => {
    const collection = selectBlockCollectionForBlock(blockId)(state);
    if (!collection) {
      return { original: undefined, working: undefined };
    }
    return {
      original: collection.original,
      working: collection.working,
    };
  };

export const selectHasBlockDataChanges =
  (blockId: string) => (state: RootState) => {
    const { original, working } = selectBlockDataForBlock(blockId)(state);
    if (!original || !working) return false;
    return JSON.stringify(original) !== JSON.stringify(working);
  };

// Field validation selectors that work with field.error and field.isLoading
export const selectFieldError = (fieldId: string) => (state: RootState) => {
  if (!state.pageV2.data?.fields) return undefined;
  const field = state.pageV2.data.fields.find((f: Field) => f.id === fieldId);
  if (field) return field.error;
  return undefined;
};

export const selectFieldIsLoading = (fieldId: string) => (state: RootState) => {
  if (!state.pageV2.data?.fields) return false;
  const field = state.pageV2.data.fields.find((f: Field) => f.id === fieldId);
  if (field) return field.isLoading === true;
  return false;
};

export const selectHasAnyFieldErrors = (state: RootState) => {
  if (!state.pageV2.data?.fields) return false;
  for (const field of state.pageV2.data.fields) {
    if (field.error) return true;
  }
  return false;
};

export const selectHasAnyFieldLoading = (state: RootState) => {
  if (!state.pageV2.data?.fields) return false;
  for (const field of state.pageV2.data.fields) {
    if (field.isLoading === true) return true;
  }
  return false;
};

export const selectTeamChatCurrentScope = (state: RootState) =>
  state.pageV2.teamChat.currentScope;
export const selectTeamChatContextTags = (state: RootState) =>
  state.pageV2.teamChat.contextTags;
export const selectTeamChatMessages = (state: RootState) =>
  state.pageV2.teamChat.messages;
export const selectTeamChatActiveTab = (state: RootState) =>
  state.pageV2.teamChat.activeTab;
export const selectTeamChatIsTyping = (state: RootState) =>
  state.pageV2.teamChat.isTyping;
export const selectImportLoading = (state: RootState) =>
  state.pageV2.import.isLoading;

export const selectInstancePageStructureEntry =
  (modelId: string) => (state: RootState) =>
    state.pageV2.instancePageStructureByModelId[modelId];

export const selectCalendarEventInstanceEntry =
  (instanceId: string) => (state: RootState) =>
    state.pageV2.calendarEventInstanceById[instanceId];

export const selectPagination = (blockId: string) => (state: RootState) => {
  const collection = selectBlockCollectionForBlock(blockId)(state);
  return collection?.ui.pagination;
};

// Export reducer
export default pageV2Slice.reducer;
